import { Paging } from '@Core';
import {
    IUpdateConfigEnableGroupMatchingResponse,
    IUpdateConfigIsVerificationsEnabledResponse,
    SortByValue,
} from '@EcamModel/controllers/ICameraHttpController';
import { ISendActionGet } from '@EcamModel/controllers/IPTZCamConfigureRequestHttpController';
import {
    CameraServiceType,
    CameraWithInfo,
    ExemptionPeriods,
    GeoZoneType,
    LocationSMStatus,
    PowerSupply,
    PurposeType,
    SolarPanelPositionType,
    Status,
    WifiStatus,
} from '@EcamModel/model';
import { CameraNoteType, CameraNotes, ExemptVehicleType } from '@EcamModel/model/CameraNotes';
import { PMC8266SysInfo } from '@EcamModel/model/PMC8266SysInfo';
import { PTZCamera, PTZPatrolMode } from '@EcamModel/model/PTZCamera';
import { SmartIrMode } from '@EcamModel/model/PTZCameraSetting';
import { NotifyItem } from '@EcamModel/model/PTZSnapshotSetting';
import { PTZSysInfo } from '@EcamModel/model/PTZSysInfo';
import { ServiceSMStatus } from '@EcamModel/model/ServiceSM';
import IcEdit from '@assets/details-camera-icons/IcEdit';
import ICDelete from '@assets/jsx-icon/ICDelete';
import BaseListNote, { tableHeaders } from '@components/BaseListNote';
import { BaseTextField } from '@components/BaseTextField';
import IOSSwitch from '@components/IOSSwitch';
import PopUpWarning from '@components/PopUpWarning';
import StyledDateRangePicker from '@components/date-range-select/StyledDateRangePicker';
import FilterRoot from '@components/filter/FilterRoot';
import { CellOfRow } from '@components/tables';
import BaseEnhancedTabs, { TabConfig } from '@components/tabs/BaseEnhancedTabs';
import { pushError, pushSuccess } from '@components/toast';
import { AxiosError } from '@controllers/axiosClient';
import {
    cameraController,
    cameraNoteHttpController,
    ptzCameraHttpController,
    sp111ConfigureRequestHttpController,
} from '@controllers/index';
import useMedia from '@hooks/useMedia';
import usePopUp, { UsePopUpReturnType } from '@hooks/usePopUp';
import {
    CheckCircle,
    ChevronLeft,
    ChevronRight,
    ErrorOutline,
    KeyboardArrowDown,
    KeyboardArrowUp,
    OpenInNew,
    SwapVert,
} from '@mui/icons-material';
import {
    Box,
    Button,
    Chip,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    Radio,
    RadioGroup,
    Stack,
    SxProps,
    TableCell,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    PositionSolarPanelLabel,
    camServiceTypes,
    positionSolarPanelReadableMapping,
    powerSupplyMapping,
} from '@pages/cameras/add-new';
import PopUpCameraStatus from '@pages/cameras/details/components/PopUpCameraStatus';
import useMileSightSolar from '@pages/cameras/hook/useMilesightSolar';
import { BaseSelect, Filter, cameraSortByStatus } from '@pages/cameras/list';
import {
    cameraPriorityReadableMapping,
    getBatteryIcon,
    purposesReadableMapping,
} from '@pages/cameras/list/components/CameraItem';
import NoDataCameras from '@pages/cameras/list/components/NoDataCameras';
import SearchTypeSelect from '@pages/cameras/list/components/SearchTypeSelect';
import { DangerTooltip } from '@pages/cameras/list/components/TooltipDangerIconButton';
import ConnectedIcon from '@pages/cameras/list/components/icons/ConnectedIcon';
import DisconnectedIcon from '@pages/cameras/list/components/icons/DisconnectedIcon';
import { CustomTabPanel } from '@pages/overview/tabs/useTabsData';
import { mappingSIMStatus, mappingSIMType } from '@pages/sim-management/utils';
import color from '@theme/Colors';
import _ from 'lodash';
import moment from 'moment';
import { Dispatch, ReactNode, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { BiHide, BiShow } from 'react-icons/bi';
import { FiSearch } from 'react-icons/fi';
import { MdAdd } from 'react-icons/md';
import { TbBook2 } from 'react-icons/tb';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import {
    formatDateByTimeZone,
    formatDateTime,
    formatTimeDayHourMinute,
    formatUKDate,
    formatUKDateTime,
    transformScheduleData,
} from 'src/helpers';
import { useBackdrop } from 'src/providers/BackdropProvider';
import { useDebounce } from 'usehooks-ts';
import { checkCellularEnum } from './CellularMilesightCameraSolar';
import { exemptVehicleTypeReadableMapping } from './exempt-vehicle-type-config';
import InformationIcon from './icon/InformationIcon';
import ImageCarousel from './ImageCarousel';
import PopUpAddExemption from './PopUpAddExemption';
import PopUpHistoryExemption from './PopUpHistoryExemption';
import PopUpNoteHistory from './PopUpNoteHistory';
import PopUpSwapMac from './PopUpSwapMac';
import PopUpEditCameraPhotoInstalled from './PupupEditCameraPhotoInstalled';
import { getHighlightColor } from './helpers';
import EvPermitExemptionSummary from './EvPermitExemptionSummary';
import {
    IrLevelDisplay,
    dnSwitchRefocusMapping,
    exposureModeMapping,
    exposureTimeMapping,
    irCutFilterMapping,
    smartIrModeMapping,
} from './tabsGeozonesDetail/ptz/milesight/components/ImageGeneralMilesightsCameraPTZ';
import { intervalUnitMapping } from './tabsGeozonesDetail/ptz/milesight/components/StorageMilesightsCameraPTZ';
import { appConfig } from '@configs/index';

type Props = {
    camera: CameraWithInfo;
    setCamera: Dispatch<SetStateAction<CameraWithInfo>>;
    onUpdateCamera?(camera: CameraWithInfo): Promise<void>;
    onUpdateService?(camera: CameraWithInfo): void;
    onAddExemption?(exemption: ExemptionPeriods): Promise<boolean> | void;
    onDeleteExemption?(exemptionId: number): Promise<boolean> | void;
    loading?: false;
    setLoading?: Dispatch<SetStateAction<boolean>>;
    isHideAction?: boolean;
};
export type FormValues = {
    name: string;
    mac: string;
    note: string;
    assets: string;
    fullAddress: string;
    solarPanelPosition: PositionSolarPanelLabel | SolarPanelPositionType;
    position: {
        lat: number;
        lng: number;
    };
};

export const LocationStatusReadableMapping = {
    [LocationSMStatus.Decommissioned]: 'Decommissioned',
    [LocationSMStatus.Live]: 'Live',
    [LocationSMStatus.OnHold]: 'On hold',
    [LocationSMStatus.Prospective]: 'Prospective',
    [LocationSMStatus.Transferred]: 'Transferred',
};

export enum Mode {
    create,
    edit,
}

export const cameraNoteSortByType: BaseSelect[] = [
    {
        Id: 1,
        Name: 'Last updated',
        Value: 'Updated',
    },
    {
        Id: 2,
        Name: 'Created date',
        Value: 'Created',
    },
];

export enum DetailsGroup {
    GENERAL,
    UPDATED,
    CREATED,
}

export type DetailsInformation = {
    id: number;
    label: string;
    value: ReactNode;
    icon?: ReactNode;
    isHide?: boolean;
    groupDetail?: DetailsGroup;
};

type Prerequisite = { label: string; isValid: boolean; href?: string };

const stellaUrl = (entity: string, id?: number) =>
    id ? `${appConfig.apps.stella}/SiteManagement/${entity}/Details/${id}` : undefined;

export default function CameraInformation(props: Props) {
    const { camera, isHideAction = false, setCamera } = props;

    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { isMediumMD, isMobileSM } = useMedia();
    const backdrop = useBackdrop();
    const { settings, cellularStatus, solar4gCamera } = useMileSightSolar();

    const popUpEditCameraPhotoInstalled = usePopUp();
    const popUpWarning = usePopUp();
    const popUpAddExemption = usePopUp();
    const popUpWarningVerification = usePopUp();
    const popUpWarningEnableGroupMatching = usePopUp();
    const popUpConfirmDelete = usePopUp();
    const popupHistoryExemption = usePopUp();
    const popupNoteHistory = usePopUp();
    const popUpConfirmDeleteNoteHistory = usePopUp();
    const popUpSwapMac = usePopUp();
    const popUpEditPatrolMode = usePopUp();

    const [mode, setMode] = useState<Mode | null>(null);
    const [isLoadingDetailsCameraPTZ, setIsLoadingDetailsCameraPTZ] = useState<boolean>(true);
    const [isLoadingDetailsCameraSP111, setIsLoadingDetailsCameraSP111] = useState<boolean>(true);
    const [selectedExemption, setSelectedExemption] = useState<ExemptionPeriods>({} as ExemptionPeriods);
    const [isDeletingExemption, setIsDeletingExemption] = useState(false);
    const [cameraDetailsPTZ, setCameraDetailsPTZ] = useState<PTZSysInfo>({} as PTZSysInfo);
    const [cameraDetailsSP111, setCameraDetailsSP111] = useState<PTZSysInfo>({} as PTZSysInfo);
    const [cameraDetailsPMC8266, setCameraDetailsPMC8266] = useState<PMC8266SysInfo>({} as PMC8266SysInfo);
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordMQTT, setShowPasswordMQTT] = useState<boolean>(false);
    const [showPasswordCellularSettings, setShowPasswordCellularSettings] = useState<boolean>(false);
    const [showPasswordSnapshotSettings, setShowPasswordSnapshotSettings] = useState<boolean>(false);

    const [filter, setFilter] = useState<Partial<Filter>>({
        page: 1,
        from: undefined,
        to: undefined,
        search: '',
        sortedOrder: cameraSortByStatus[1],
        sort: cameraNoteSortByType[0],
    });

    const [valueTabNotes, setValueTabNotes] = useState<number>(0);
    const [idNote, setIdNote] = useState<number>(0);
    const [searchValue, setSearchValue] = useState<string>('');
    const debouncedValue = useDebounce<string>(searchValue ?? '', 500);

    const [isActive, setIsActive] = useState(camera.Status);
    const [isVerification, setIsVerification] = useState<IUpdateConfigIsVerificationsEnabledResponse>({
        IsVerificationsEnabled: camera.IsVerificationsEnabled!,
        EnablePcnVerificationAt: camera.EnablePcnVerificationAt!,
    });
    const [isEnableGroupMatching, setIsEnableGroupMatching] = useState<IUpdateConfigEnableGroupMatchingResponse>({
        EnableGroupMatchingAt: camera.EnableGroupMatchingAt!,
    });
    const [isEnabled, setIsEnabled] = useState(true);

    const [patrolMode, setPatrolMode] = useState<PTZPatrolMode>(
        camera.PTZCamera?.PatrolMode ?? PTZPatrolMode.FullPatrol
    );

    const getStatusColor = (status: PurposeType) => {
        const colorRender = (props: { color: string; bgcolor: string }) => ({
            colorText: props.color,
            bgColor: props.bgcolor,
        });

        const mappingColor = {
            [PurposeType.Prospective]: colorRender({
                color: color.secondary,
                bgcolor: '#E5F2FF',
            }),
            [PurposeType.Live]: colorRender({
                bgcolor: '#E5F5ED',
                color: '#009D4F',
            }),
            [PurposeType.Testing]: colorRender({
                bgcolor: '#CC00661A',
                color: '#CC0066',
            }),
            [PurposeType.Decommissioned]: colorRender({
                bgcolor: '#FCE8E5',
                color: '#E01B00',
            }),
            [PurposeType.OnHold]: colorRender({
                bgcolor: '#FFF8E5',
                color: '#FFB600',
            }),
        };
        return (
            mappingColor[status] ||
            colorRender({
                bgcolor: '#FCE8E5',
                color: '#E01B00',
            })
        );
    };

    const { colorText, bgColor } = getStatusColor(camera.PurposeType!);

    const sortingConditions =
        filter.sortedOrder?.Value === SortByValue.Asc ? `${filter.sort?.Value}` : `-${filter.sort?.Value}`;

    const handleGetDetailsCameraSP111 = async () => {
        const _filter: ISendActionGet = {
            ip: camera.IP,
            authenticate: {
                password: camera.PassWord,
                username: camera.UserName,
            },
        };
        setIsLoadingDetailsCameraSP111(true);
        await sp111ConfigureRequestHttpController
            .getSP111SystemInfo(_filter)
            .then((res) => {
                setCameraDetailsSP111(res);
            })
            .finally(() => {
                setIsLoadingDetailsCameraSP111(false);
            });
    };

    const handleOnConfirm = (isActiveAllGeoZones?: boolean) => {
        cameraController
            .changeStatus({ CameraId: camera.Id!, IsActiveAllGeoZones: isActiveAllGeoZones })
            .then((res) => {
                queryClient.invalidateQueries(['trailAuditEvent']);
                pushSuccess('Changed status successfully');
                setIsActive(res.Status);
                const shouldSyncGeoZones = res.Status === Status.Inactive || !!isActiveAllGeoZones;
                setCamera((prev) => ({
                    ...prev,
                    Status: res.Status,
                    ...(shouldSyncGeoZones && {
                        GeoZones: prev.GeoZones?.map((gz) => ({ ...gz, Status: res.Status })),
                    }),
                }));
            })
            .catch((err) => {})
            .finally(() => {
                popUpWarning.onClose();
            });
    };
    const handleConfirmVerification = async (cameraId: number, isEnabled: boolean) => {
        backdrop.setTrue();
        await cameraController
            .updateConfigIsVerificationsEnabled({ CameraId: cameraId, Enable: isEnabled })
            .then((res) => {
                queryClient.invalidateQueries(['trailAuditEvent']);
                setIsVerification(res);
                pushSuccess('Updated successfully');
                setCamera((prev) => ({
                    ...prev,
                    IsVerificationsEnabled: res.IsVerificationsEnabled,
                    EnablePcnVerificationAt: res.EnablePcnVerificationAt,
                }));
            })
            .catch(() => {})
            .finally(() => {
                backdrop.setFalse();
            });
    };

    const handleConfirmEnableGroupMatching = async (cameraId: number, isEnabled: boolean) => {
        backdrop.setTrue();
        await cameraController
            .updateConfigEnableGroupMatching({ CameraId: cameraId, Enable: isEnabled })
            .then((res) => {
                queryClient.invalidateQueries(['trailAuditEvent']);
                setIsEnableGroupMatching(res);
                pushSuccess('Updated successfully');
                setCamera((prev) => ({
                    ...prev,
                    EnableGroupMatchingAt: res.EnableGroupMatchingAt,
                }));
            })
            .catch((err) => {
                pushError(err.response.data.message);
            })
            .finally(() => {
                backdrop.setFalse();
                popUpWarningEnableGroupMatching.onClose();
            });
    };

    const handleGetListNote = async () => {
        const response = await cameraNoteHttpController.list({
            page: filter.page ?? 1,
            CameraId: camera.Id ?? 0,
            pageSize: 8,
            filter: {
                CameraId: camera.Id ?? 0,
                Type: valueTabNotes === 0 ? CameraNoteType.Camera : CameraNoteType.Verification,
            },
            search: {
                content: searchValue ?? '',
                fields: ['CreatedBy'],
            },
            sorts: [sortingConditions] as any,
            From: filter.from ?? undefined,
            To: filter.to ?? undefined,
        });
        return response;
    };

    const upsertCameraNoteAPI = async (data: CameraNotes) => {
        const response = await cameraNoteHttpController.upsert(data);
        return response;
    };

    const mutationUpsertCameraNote = useMutation(upsertCameraNoteAPI, {
        onSuccess: async (data, variables) => {
            queryClient.invalidateQueries(['listNotes']);
            queryClient.invalidateQueries(['trailAuditEvent']);
            pushSuccess(variables.Id ? 'Update successfully' : 'Add successfully');
        },
        onError: (err: AxiosError) => {
            pushError(err.message);
        },
        onSettled: () => {
            backdrop.setFalse();
        },
    });

    const handleAddOrUpdateNote = async (
        idCamera: number,
        note: string,
        idNote?: number,
        exemptVehicleTypes?: ExemptVehicleType[]
    ) => {
        backdrop.setTrue();
        const type = valueTabNotes === 0 ? CameraNoteType.Camera : CameraNoteType.Verification;
        const data: CameraNotes = {
            CameraId: idCamera,
            Notes: note,
            Id: mode === Mode.edit ? idNote : undefined,
            Type: type,
            ExemptVehicleTypes: type === CameraNoteType.Verification ? (exemptVehicleTypes ?? []) : undefined,
        };

        await mutationUpsertCameraNote.mutateAsync(data);
    };

    const deleteNoteAPI = async (idNote: number) => {
        const response = await cameraNoteHttpController.delete(idNote.toString());
        return response;
    };

    const mutationDeleteNote = useMutation(deleteNoteAPI, {
        onSuccess: async () => {
            queryClient.invalidateQueries(['listNotes']);
            queryClient.invalidateQueries(['trailAuditEvent']);
            pushSuccess('Delete successfully');
        },
        onError: (err: AxiosError) => {
            pushError(err.message);
        },
        onSettled: () => {
            backdrop.setFalse();
        },
    });

    const handleDeleteNote = async (idNote: number) => {
        backdrop.setTrue();
        await mutationDeleteNote.mutateAsync(idNote);
    };

    const handleEditPatrolMode = async (patrolMode: number) => {
        backdrop.setTrue();
        await ptzCameraHttpController
            .updatePatrolMode({ DevMac: camera.PTZCamera?.Mac!, PatrolMode: patrolMode })
            .then((res) => {
                queryClient.invalidateQueries(['trailAuditEvent']);
                pushSuccess('Updated successfully');
                setCamera({ ...camera, PTZCamera: { ...camera.PTZCamera, PatrolMode: res.PatrolMode } as PTZCamera });
            })
            .catch((err) => {
                pushError(err.message?.data?.message);
            })
            .finally(() => {
                backdrop.setFalse();
                popUpEditPatrolMode.onClose();
            });
    };

    const handleRadioChangePatrolMode = (event: React.ChangeEvent<HTMLInputElement>) => {
        popUpEditPatrolMode.setTrue();
        setPatrolMode(Number(event.target.value) as PTZPatrolMode);
    };

    const isCameraLocationDecommissioned = camera.Zone?.Location?.LocationStatus === LocationSMStatus.Decommissioned;

    const HorizontalScrollChips = ({ children }: { children: React.ReactNode }) => {
        const scrollRef = useRef<HTMLDivElement>(null);
        const [showFade, setShowFade] = useState(true);
        const [showPrev, setShowPrev] = useState(false);
        const [showNext, setShowNext] = useState(false);

        const handleScroll = () => {
            if (scrollRef.current) {
                const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
                setShowFade(scrollLeft + clientWidth < scrollWidth - 5);
                setShowPrev(scrollLeft > 5);
                setShowNext(scrollLeft + clientWidth < scrollWidth - 5);
            }
        };

        const scrollTo = (direction: 'prev' | 'next') => {
            if (scrollRef.current) {
                const scrollAmount = 200;
                const newScrollLeft =
                    direction === 'next'
                        ? scrollRef.current.scrollLeft + scrollAmount
                        : scrollRef.current.scrollLeft - scrollAmount;

                scrollRef.current.scrollTo({
                    left: newScrollLeft,
                    behavior: 'smooth',
                });
            }
        };

        useEffect(() => {
            const element = scrollRef.current;
            if (element) {
                const isScrollable = element.scrollWidth > element.clientWidth;
                setShowFade(isScrollable);
                setShowNext(isScrollable);
                setShowPrev(false);
            }
        }, [children]);

        return (
            <Box
                sx={{
                    position: 'relative',
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        bottom: 0,
                        width: '40px',
                        background: 'linear-gradient(to right, transparent, white)',
                        pointerEvents: 'none',
                        opacity: showFade ? 1 : 0,
                        transition: 'opacity 0.3s ease',
                    },
                }}
            >
                {/* Previous Button */}
                {showPrev && (
                    <IconButton
                        onClick={() => scrollTo('prev')}
                        sx={{
                            position: 'absolute',
                            left: -8,
                            top: '45%',
                            transform: 'translateY(-50%)',
                            zIndex: 2,
                            bgcolor: 'white',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            width: 20,
                            height: 20,
                            '&:hover': {
                                bgcolor: color.grey100,
                            },
                        }}
                    >
                        <ChevronLeft sx={{ fontSize: 16 }} />
                    </IconButton>
                )}

                {/* Next Button */}
                {showNext && (
                    <IconButton
                        onClick={() => scrollTo('next')}
                        sx={{
                            position: 'absolute',
                            right: -8,
                            top: '45%',
                            transform: 'translateY(-50%)',
                            zIndex: 2,
                            bgcolor: 'white',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            width: 20,
                            height: 20,
                            '&:hover': {
                                bgcolor: color.grey100,
                            },
                        }}
                    >
                        <ChevronRight sx={{ fontSize: 16 }} />
                    </IconButton>
                )}

                <Stack
                    ref={scrollRef}
                    direction="row"
                    spacing={1}
                    onScroll={handleScroll}
                    sx={{
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        pt: 0.5,
                        pb: 0.5,
                        pr: showFade ? 2 : 0,
                        '&::-webkit-scrollbar': {
                            display: 'none',
                        },
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                    }}
                >
                    {children}
                </Stack>
            </Box>
        );
    };

    const VerticalScrollContainer = ({ children }: { children: React.ReactNode }) => {
        const scrollRef = useRef<HTMLDivElement>(null);
        const [showUp, setShowUp] = useState(false);
        const [showDown, setShowDown] = useState(false);

        const handleScroll = () => {
            if (scrollRef.current) {
                const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
                setShowUp(scrollTop > 5);
                setShowDown(scrollTop + clientHeight < scrollHeight - 5);
            }
        };

        const scrollTo = (direction: 'up' | 'down') => {
            if (scrollRef.current) {
                const scrollAmount = 80;
                const newScrollTop =
                    direction === 'down'
                        ? scrollRef.current.scrollTop + scrollAmount
                        : scrollRef.current.scrollTop - scrollAmount;

                scrollRef.current.scrollTo({
                    top: newScrollTop,
                    behavior: 'smooth',
                });
            }
        };

        useEffect(() => {
            const element = scrollRef.current;
            if (element) {
                const isScrollable = element.scrollHeight > element.clientHeight;
                setShowDown(isScrollable);
                setShowUp(false);
            }
        }, [children]);

        return (
            <Box sx={{ position: 'relative' }}>
                {/* Up Button */}
                {showUp && (
                    <IconButton
                        onClick={() => scrollTo('up')}
                        sx={{
                            position: 'absolute',
                            top: -8,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            zIndex: 2,
                            bgcolor: 'white',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            width: 20,
                            height: 20,
                            '&:hover': {
                                bgcolor: color.grey100,
                            },
                        }}
                    >
                        <KeyboardArrowUp sx={{ fontSize: 16 }} />
                    </IconButton>
                )}

                {/* Down Button */}
                {showDown && (
                    <IconButton
                        onClick={() => scrollTo('down')}
                        sx={{
                            position: 'absolute',
                            bottom: -8,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            zIndex: 2,
                            bgcolor: 'white',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            width: 20,
                            height: 20,
                            '&:hover': {
                                bgcolor: color.grey100,
                            },
                        }}
                    >
                        <KeyboardArrowDown sx={{ fontSize: 16 }} />
                    </IconButton>
                )}

                <Box
                    ref={scrollRef}
                    onScroll={handleScroll}
                    sx={{
                        maxHeight: '140px',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        '&::-webkit-scrollbar': {
                            display: 'none',
                        },
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                    }}
                >
                    {children}
                </Box>
            </Box>
        );
    };

    const tabCrossZoneCoverage: DetailsInformation[] = [
        {
            id: 1,
            label: 'Cross-Zone Coverage',
            value: (
                <VerticalScrollContainer>
                    <Stack spacing={2}>
                        {camera.ExtendedCoverage?.map((item, index) => (
                            <Box key={index}>
                                {/* Location */}
                                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                                    <Chip
                                        label={item.Name}
                                        size="small"
                                        sx={{
                                            bgcolor: 'hsl(210 80% 52% / 0.12)',
                                            color: 'hsl(210 80% 52%)',
                                            border: '1px solid hsl(210 80% 52% / 0.25)',
                                            fontWeight: 600,
                                            flexShrink: 0,
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            width: '20px',
                                            height: '2px',
                                            bgcolor: color.grey300,
                                        }}
                                    />
                                    <Typography variant="caption" color={color.grey400} sx={{ fontWeight: 500 }}>
                                        {item.Zones?.length} {item.Zones?.length === 1 ? 'zone' : 'zones'}
                                    </Typography>
                                </Stack>

                                {/* Zones for this location */}
                                {item.Zones && item.Zones.length > 0 && (
                                    <Box sx={{ pl: 1, borderLeft: `2px solid ${color.grey200}` }}>
                                        <HorizontalScrollChips>
                                            {item.Zones.map((zone, zoneIndex) => (
                                                <Chip
                                                    key={zoneIndex}
                                                    label={zone.PublicName}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: 'hsl(170 70% 42% / 0.12)',
                                                        color: 'hsl(170 70% 42%)',
                                                        border: '1px solid hsl(170 70% 42% / 0.25)',
                                                        fontWeight: 500,
                                                        flexShrink: 0,
                                                    }}
                                                />
                                            ))}
                                        </HorizontalScrollChips>
                                    </Box>
                                )}
                            </Box>
                        ))}
                    </Stack>
                </VerticalScrollContainer>
            ),
        },
    ];

    const detailHeaderTop: DetailsInformation[] = [
        {
            id: 1,
            label: 'Camera type',
            value: (
                <Typography variant="body1" color={'#85858A'}>
                    {camera.VendorCameraId !== null
                        ? 'Camera Solar'
                        : camera.PTZCameraId !== null
                          ? 'View 360'
                          : camera.SP111CameraId !== null
                            ? 'Camera SP111'
                            : 'Camera PMC8266'}
                </Typography>
            ),
        },
        {
            id: 2,
            label: 'Status',
            value: (
                <>
                    {isHideAction ? (
                        <Stack
                            sx={{
                                width: 80,
                                height: 30,
                                background: (theme) => theme.palette.error.main,
                                borderRadius: 1,
                            }}
                            justifyContent="center"
                            alignItems="center"
                        >
                            <Typography color="#FFFFFF">Deleted</Typography>
                        </Stack>
                    ) : (
                        <Box display="flex" alignItems="center">
                            <Typography variant="body1" color={'#85858A'} sx={{ marginRight: 1 }}>
                                {Boolean(isActive === Status.Active) ? 'Active' : 'Inactive'}
                            </Typography>
                            <IOSSwitch
                                iosSwitchProps={{
                                    checked: Boolean(isActive === Status.Active),
                                    onChange(event, checked) {
                                        popUpWarning.setTrue();
                                    },
                                    disabled:
                                        moment().isBefore(camera.DeployedDate) ||
                                        Boolean(isActive === Status.NotAvailable),
                                }}
                            />
                        </Box>
                    )}
                </>
            ),
        },
        {
            id: 3,
            label: 'Battery',
            value: (
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body1" color={'#85858A'}>
                        {camera.Solar4gCamera?.LastBatteryStatus !== undefined
                            ? `${camera.Solar4gCamera.LastBatteryStatus}%`
                            : 'Not available'}
                    </Typography>
                    {getBatteryIcon(camera.Solar4gCamera?.LastBatteryStatus ?? 0)}
                </Stack>
            ),
        },
        {
            id: 4,
            label: 'Priority',
            value: (
                <Typography variant="body1" color={'#85858A'}>
                    {camera.Priority !== null ? cameraPriorityReadableMapping[camera.Priority!] : 'None'}
                </Typography>
            ),
        },
        {
            id: 5,
            label: 'Network',
            value: (
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body1" color={'#85858A'}>
                        {camera.Solar4gCamera?.LastWifiStatus === WifiStatus.Connected ? 'Connected' : 'Not available'}
                    </Typography>
                    {camera.Solar4gCamera?.LastWifiStatus === WifiStatus.Connected ? (
                        <ConnectedIcon width={18} height={18} />
                    ) : (
                        <DisconnectedIcon width={18} height={18} />
                    )}
                </Stack>
            ),
        },
    ];

    const lastContact =
        camera.VendorCameraId !== null
            ? camera.Solar4gCamera?.LastContact
            : camera.PTZCameraId !== null
              ? camera.PTZCamera?.LastContact
              : null;

    const detailHeaderBot: DetailsInformation[] = [
        {
            id: 1,
            label: 'Created by',
            value: (
                <Typography variant="body1" color={'#85858A'}>
                    {camera.CreatedBy}
                </Typography>
            ),
        },
        {
            id: 2,
            label: 'Created at',
            value: (
                <Typography variant="body1" color={'#85858A'}>
                    {formatUKDateTime(camera.Created)}
                </Typography>
            ),
        },
        {
            id: 3,
            label: 'Last contact',
            value: (
                <Typography variant="body1" color={'#85858A'}>
                    {formatUKDateTime(lastContact)}
                </Typography>
            ),
        },
    ];

    const tabDetailsCameraSolar: DetailsInformation[] = [
        {
            id: 1,
            label: 'Name',
            value: <Typography variant="body1">{camera.Name ?? ''}</Typography>,
        },
        {
            id: 2,
            label: 'MAC',
            value: (
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent={{ xs: 'space-between', sm: 'flex-start' }}
                    spacing={{ xs: 1, sm: 4 }}
                >
                    <Typography>{camera.Mac}</Typography>

                    {camera?.Status === Status.Active && (
                        <Tooltip placement="top" title="Swap MAC camera">
                            <SwapVert
                                style={{
                                    color: color.grey600,
                                }}
                                fontSize="small"
                                cursor="pointer"
                                onClick={() => {
                                    popUpSwapMac.setTrue();
                                }}
                            />
                        </Tooltip>
                    )}
                </Stack>
            ),
        },
        {
            id: 3,
            label: 'Purposes',
            value: (
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Chip
                        size="small"
                        sx={{
                            borderRadius: '16px',
                            fontSize: 12,
                            p: '5px',
                            background: bgColor,
                            color: colorText,
                        }}
                        label={purposesReadableMapping?.[camera.PurposeType!] ?? 'N/A'}
                    />
                </Stack>
            ),
        },
        {
            id: 4,
            label: 'Asset',
            value: <Typography>{camera.Asset}</Typography>,
        },
        {
            id: 5,
            label: 'Lens capacity',
            value: <Typography>{camera.LensCapacity}</Typography>,
        },
        {
            id: 6,
            label: 'Firmware version',
            value: <Typography>{camera.Solar4gCamera?.Solar4gSysInfo?.Software}</Typography>,
        },
        {
            id: 7,
            label: 'Power supply',
            value: <Typography>{powerSupplyMapping[camera?.PowerSupply!]?.label || ''}</Typography>,
        },
        {
            id: 7,
            label: 'Solar panel position',
            value: (
                <Typography>{positionSolarPanelReadableMapping[camera?.SolarPanelPosition!]?.label || ''}</Typography>
            ),
            isHide: camera.PowerSupply === PowerSupply.Wired,
        },
    ];

    const tabDetailsCameraPTZ: DetailsInformation[] = [
        {
            id: 1,
            label: 'Name',
            value: <Typography variant="body1">{camera.Name}</Typography>,
        },
        {
            id: 2,
            label: 'MAC',
            value: <Typography>{camera.Mac}</Typography>,
        },
        {
            id: 3,
            label: 'Purposes',
            value: (
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Chip
                        size="small"
                        sx={{
                            borderRadius: '16px',
                            fontSize: 12,
                            p: '5px',
                            background: bgColor,
                            color: colorText,
                        }}
                        label={purposesReadableMapping[camera.PurposeType!] ?? 'N/A'}
                    />
                </Stack>
            ),
        },
        {
            id: 4,
            label: 'Asset',
            value: <Typography>{camera.Asset}</Typography>,
        },
        {
            id: 5,
            label: 'Lens capacity',
            value: <Typography>{camera.LensCapacity}</Typography>,
        },
        {
            id: 6,
            label: 'Product model',
            value: <Typography>{cameraDetailsPTZ.Model}</Typography>,
        },
        {
            id: 7,
            label: 'Hardware version',
            value: <Typography>{cameraDetailsPTZ.HardwareVersion}</Typography>,
        },
        {
            id: 8,
            label: 'Firmware version',
            value: <Typography>{cameraDetailsPTZ.FirmwareVersion}</Typography>,
        },
        {
            id: 9,
            label: 'S/N',
            value: <Typography>{cameraDetailsPTZ.SnCode}</Typography>,
        },
        {
            id: 10,
            label: 'Device information',
            value: <Typography>{cameraDetailsPTZ.DeviceInformation}</Typography>,
        },

        {
            id: 11,
            label: 'Alarm input',
            value: <Typography>{cameraDetailsPTZ.AlarmInputSupport}</Typography>,
        },
        {
            id: 12,
            label: 'Alarm output',
            value: <Typography>{cameraDetailsPTZ.AlarmOutputSupport}</Typography>,
        },
        {
            id: 13,
            label: 'Uptime',
            value: <Typography>{formatTimeDayHourMinute(cameraDetailsPTZ.Runtime)}</Typography>,
        },
    ];

    const tabDetailsCameraSP111: DetailsInformation[] = [
        {
            id: 1,
            label: 'Name',
            value: <Typography variant="body1">{camera.Name}</Typography>,
        },
        {
            id: 2,
            label: 'MAC',
            value: <Typography>{camera.Mac}</Typography>,
        },
        {
            id: 3,
            label: 'Purposes',
            value: (
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Chip
                        size="small"
                        sx={{
                            borderRadius: '16px',
                            fontSize: 12,
                            p: '5px',
                            background: bgColor,
                            color: colorText,
                        }}
                        label={purposesReadableMapping[camera.PurposeType!] ?? 'N/A'}
                    />
                </Stack>
            ),
        },
        {
            id: 4,
            label: 'Asset',
            value: <Typography>{camera.Asset}</Typography>,
        },
        {
            id: 5,
            label: 'Lens capacity',
            value: <Typography>{camera.LensCapacity}</Typography>,
        },
        {
            id: 6,
            label: 'Product model',
            value: <Typography>{cameraDetailsSP111.Model}</Typography>,
        },
        {
            id: 7,
            label: 'Hardware version',
            value: <Typography>{cameraDetailsSP111.HardwareVersion}</Typography>,
        },
        {
            id: 8,
            label: 'Firmware version',
            value: <Typography>{cameraDetailsSP111.FirmwareVersion}</Typography>,
        },
        {
            id: 9,
            label: 'S/N',
            value: <Typography>{cameraDetailsSP111.SnCode}</Typography>,
        },
        {
            id: 10,
            label: 'Device information',
            value: <Typography>{cameraDetailsSP111.DeviceInformation}</Typography>,
        },

        {
            id: 11,
            label: 'Alarm input',
            value: <Typography>{cameraDetailsSP111.AlarmInputSupport}</Typography>,
        },
        {
            id: 12,
            label: 'Alarm output',
            value: <Typography>{cameraDetailsSP111.AlarmOutputSupport}</Typography>,
        },
        {
            id: 13,
            label: 'Uptime',
            value: <Typography>{formatTimeDayHourMinute(cameraDetailsSP111.Runtime)}</Typography>,
        },
    ];

    const tabDetailsCameraPMC8266: DetailsInformation[] = [
        {
            id: 1,
            label: 'Name',
            value: <Typography variant="body1">{camera.Name}</Typography>,
        },
        {
            id: 2,
            label: 'MAC',
            value: <Typography>{camera.Mac}</Typography>,
        },
        {
            id: 3,
            label: 'Purposes',
            value: (
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Chip
                        size="small"
                        sx={{
                            borderRadius: '16px',
                            fontSize: 12,
                            p: '5px',
                            background: bgColor,
                            color: colorText,
                        }}
                        label={purposesReadableMapping[camera.PurposeType!] ?? 'N/A'}
                    />
                </Stack>
            ),
        },
        {
            id: 4,
            label: 'Asset',
            value: <Typography>{camera.Asset}</Typography>,
        },
        {
            id: 5,
            label: 'Lens capacity',
            value: <Typography>{camera.LensCapacity}</Typography>,
        },
        {
            id: 6,
            label: 'IP address',
            value: <Typography>{cameraDetailsPMC8266?.Ipaddress}</Typography>,
        },
        {
            id: 7,
            label: 'Kernel version',
            value: <Typography>{cameraDetailsPMC8266?.KernelVersion}</Typography>,
        },
        {
            id: 8,
            label: 'Device model',
            value: <Typography>{cameraDetailsPMC8266?.Model}</Typography>,
        },
        {
            id: 9,
            label: 'Device name',
            value: <Typography>{cameraDetailsPMC8266?.DeviceName}</Typography>,
        },
        {
            id: 14,
            label: 'Device information',
            value: <Typography>{cameraDetailsPMC8266?.DeviceInformation}</Typography>,
        },
        {
            id: 10,
            label: 'Firmware version',
            value: <Typography>{cameraDetailsPMC8266?.FirmwareVersion}</Typography>,
        },

        {
            id: 11,
            label: 'Hardware version',
            value: <Typography>{cameraDetailsPMC8266?.HardwareVersion}</Typography>,
        },
        {
            id: 12,
            label: 'Gateway',
            value: <Typography>{cameraDetailsPMC8266?.Gateway}</Typography>,
        },
        {
            id: 13,
            label: 'Uptime',
            value: <Typography>{formatTimeDayHourMinute(cameraDetailsPMC8266?.Runtime)}</Typography>,
        },
    ];

    const tabAllocatedZone: DetailsInformation[] = [
        {
            id: 1,
            label: 'Location',
            value: <Typography>{camera.LocationName}</Typography>,
        },
        {
            id: 2,
            label: 'Zone',
            value: <Typography>{camera.ZoneName}</Typography>,
        },
        {
            id: 4,
            label: 'Time zone',
            value: <Typography>{camera.TimeZone || 'N/A'}</Typography>,
        },
        // ...(camera.PTZCameraId
        //     ? [
        //           {
        //               id: 4,
        //               label: 'Parking',
        //               value: <Typography>{camera?.ParkingSpace?.Name ?? 'None'}</Typography>,
        //           },
        //       ]
        //     : []),
        {
            id: 3,
            label: 'Location status',
            value: (
                <Typography
                    bgcolor={isCameraLocationDecommissioned ? color.danger : 'transparent'}
                    color={isCameraLocationDecommissioned ? color.white : color.textPrimary}
                    p={isCameraLocationDecommissioned ? '4px 10px' : 0}
                    borderRadius={1}
                    maxWidth={'max-content'}
                >
                    {camera.Zone?.Location?.LocationStatus !== null
                        ? LocationStatusReadableMapping[camera?.Zone?.Location?.LocationStatus ?? 0]
                        : 'N/A'}
                </Typography>
            ),
        },
    ];

    const zone = camera.Zone;
    const services = zone?.Services ?? [];

    const productionService = services.find((s) => s.Status === ServiceSMStatus.Production);
    const linkedService = productionService ?? services[0];

    const locationId = zone?.Location?.Id ?? zone?.LocationId;
    const zoneId = zone?.Id ?? camera.ZoneId;

    // No service yet: link to the zone so the user can set one up in Stella
    const serviceHref = stellaUrl('Service', linkedService?.Id) ?? stellaUrl('Zone', zoneId);

    const isPTZCamera = camera.ServiceType === CameraServiceType.PTZ;
    const relevantGeoZones = (camera.GeoZones ?? []).filter(
        (gz) => !isPTZCamera || gz.GeoZoneType === GeoZoneType.Detail
    );
    const hasGeoZone = relevantGeoZones.length > 0;
    const hasActiveGeoZone = relevantGeoZones.some((gz) => gz.Status === Status.Active);

    const verificationPrerequisites: Prerequisite[] = [
        {
            label: 'Location status: Live',
            isValid: zone?.Location?.LocationStatus === LocationSMStatus.Live,
            href: stellaUrl('Location', locationId),
        },
        { label: 'Services status: Production', isValid: !!productionService, href: serviceHref },
        { label: 'Camera status: Active', isValid: isActive === Status.Active },
        { label: `Geo-Zones ${hasGeoZone ? 'active' : 'setup'}`, isValid: hasActiveGeoZone },
    ];
    const isVerificationBlocked =
        !isVerification.IsVerificationsEnabled && verificationPrerequisites.some((item) => !item.isValid);

    const createVerificationField = (): DetailsInformation => ({
        id: 1,
        label: 'Enable verifications',
        value: (
            <>
                {isHideAction ? (
                    <Typography>On</Typography>
                ) : (
                    <Stack direction={'row'} alignItems={'center'}>
                        <IOSSwitch
                            label={
                                <Typography sx={{ minWidth: '55px' }}>
                                    {!!isVerification.IsVerificationsEnabled ? 'On' : 'Off'}
                                </Typography>
                            }
                            iosSwitchProps={{
                                checked: isVerification.IsVerificationsEnabled,
                                disabled: isVerificationBlocked,
                                onChange(event, checked) {
                                    setIsEnabled(checked);
                                    popUpWarningVerification.setTrue();
                                },
                            }}
                        />
                        <Tooltip
                            placement="top"
                            arrow
                            title={
                                <Stack spacing={0.5}>
                                    {verificationPrerequisites.map((item) => (
                                        <Stack
                                            key={item.label}
                                            direction={'row'}
                                            alignItems={'center'}
                                            spacing={1}
                                            {...(item.href && {
                                                component: 'a',
                                                href: item.href,
                                                target: '_blank',
                                                rel: 'noopener noreferrer',
                                            })}
                                            sx={{
                                                // px/mx cancel out so icons stay aligned with the tooltip edge
                                                px: 0.75,
                                                mx: -0.75,
                                                py: 0.25,
                                                borderRadius: 0.5,
                                                color: color.white,
                                                textDecoration: 'none',
                                                ...(item.href && {
                                                    '&:hover': {
                                                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                                                    },
                                                }),
                                            }}
                                        >
                                            {item.isValid ? (
                                                <CheckCircle sx={{ fontSize: 16, color: '#4ADE80' }} />
                                            ) : (
                                                <ErrorOutline sx={{ fontSize: 16, color: '#FF6B6B' }} />
                                            )}
                                            <Typography
                                                className="prerequisite-label"
                                                variant="body2"
                                                color={color.white}
                                                flex={1}
                                            >
                                                {item.label}
                                            </Typography>
                                            {item.href && <OpenInNew sx={{ fontSize: 14, opacity: 0.6 }} />}
                                        </Stack>
                                    ))}
                                </Stack>
                            }
                        >
                            <Box display={'flex'} sx={{ cursor: 'pointer' }}>
                                <InformationIcon />
                            </Box>
                        </Tooltip>
                    </Stack>
                )}
            </>
        ),
    });

    const createVerificationAtField = (): DetailsInformation => ({
        id: 2,
        label: 'Enable verifications at',
        value: (
            <Typography sx={{ minWidth: '55px' }}>
                {isVerification.EnablePcnVerificationAt || camera.EnablePcnVerificationAt
                    ? formatUKDateTime(isVerification.EnablePcnVerificationAt)
                    : 'N/A'}
            </Typography>
        ),
    });

    const createGroupMatchingField = (): DetailsInformation => ({
        id: 3,
        label: 'Enable group matching',
        value: (
            <>
                {isHideAction ? (
                    <Typography>On</Typography>
                ) : (
                    <IOSSwitch
                        label={
                            <Typography sx={{ minWidth: '55px' }}>
                                {!!isEnableGroupMatching.EnableGroupMatchingAt ? 'On' : 'Off'}
                            </Typography>
                        }
                        iosSwitchProps={{
                            checked: Boolean(isEnableGroupMatching.EnableGroupMatchingAt),
                            onChange(event, checked) {
                                setIsEnabled(checked);
                                popUpWarningEnableGroupMatching.setTrue();
                            },
                        }}
                    />
                )}
            </>
        ),
    });

    const createGroupMatchingAtField = (): DetailsInformation => ({
        id: 4,
        label: 'Enable group matching at',
        value: (
            <Typography sx={{ minWidth: '55px' }}>
                {isEnableGroupMatching.EnableGroupMatchingAt || camera.EnableGroupMatchingAt
                    ? formatUKDateTime(isEnableGroupMatching.EnableGroupMatchingAt)
                    : 'N/A'}
            </Typography>
        ),
    });

    const createPatrolModeField = (): DetailsInformation => ({
        id: 5,
        label: 'Patrol mode',
        value: (
            <FormControl>
                <RadioGroup
                    row
                    value={camera.PTZCamera?.PatrolMode}
                    onChange={handleRadioChangePatrolMode}
                    sx={{ alignContent: 'center', alignItems: 'center', gap: 1 }}
                >
                    <Tooltip
                        placement="top"
                        arrow
                        componentsProps={{
                            tooltip: {
                                sx: {
                                    maxWidth: 440,
                                },
                            },
                        }}
                        title={
                            <Box>
                                <Typography variant="body1" color={color.white}>
                                    The View360 camera automatically patrols through all presets, including both{' '}
                                    <strong>Overview</strong> and <strong>Detail View</strong> positions, capturing
                                    photo at each point.
                                </Typography>
                                <Typography sx={{ mt: 0.5 }} variant="body1" color={color.white}>
                                    → Best for full-area monitoring or when you need to record every movement,
                                    regardless of whether a vehicle is present.
                                </Typography>
                            </Box>
                        }
                    >
                        <FormControlLabel
                            sx={{
                                '.MuiFormControlLabel-label': {
                                    mt: '3px !important',
                                },
                            }}
                            control={<Radio value={PTZPatrolMode.FullPatrol} />}
                            label={'Full patrol mode'}
                        />
                    </Tooltip>

                    <Tooltip
                        placement="top"
                        arrow
                        componentsProps={{
                            tooltip: {
                                sx: {
                                    maxWidth: 440,
                                },
                            },
                        }}
                        title={
                            <Box>
                                <Typography variant="body1" color={color.white}>
                                    The View360 camera patrols only through <strong>Overview presets</strong>. When the
                                    AI detects a <strong>vehicle</strong>, it automatically{' '}
                                    <strong>zooms in to the corresponding detail preset</strong> to capture a close-up
                                    photo.
                                </Typography>
                                <Typography sx={{ mt: 0.5 }} variant="body1" color={color.white}>
                                    → Best for reducing the number of photos and focusing only on actual vehicle events.
                                </Typography>
                            </Box>
                        }
                    >
                        <FormControlLabel
                            sx={{
                                '.MuiFormControlLabel-label': {
                                    mt: '3px !important',
                                },
                            }}
                            control={<Radio value={PTZPatrolMode.VehicleOnlyPatrol} />}
                            label={'Smart detect mode'}
                        />
                    </Tooltip>
                </RadioGroup>
            </FormControl>
        ),
    });

    const tabDataProcessing: DetailsInformation[] = useMemo(() => {
        const baseFields = [
            createVerificationField(),
            createVerificationAtField(),
            createGroupMatchingField(),
            createGroupMatchingAtField(),
            ...(camera.PTZCameraId !== null ? [createPatrolModeField()] : []),
        ];

        return baseFields;
    }, [
        isHideAction,
        isVerification,
        isEnableGroupMatching,
        camera.PTZCameraId,
        camera.PTZCamera?.PatrolMode,
        camera.EnablePcnVerificationAt,
        camera.EnableGroupMatchingAt,
        handleRadioChangePatrolMode,
    ]);

    const tabLoginInformation: DetailsInformation[] = [
        {
            id: 1,
            label: 'IP',
            value: (
                <Tooltip
                    title={
                        !_.isNil(camera.VendorCameraId) && solar4gCamera?.Solar4gCellularStatus?.IpAddr != camera.IP
                            ? `IP addresses differ between Cellular Status and Base Information. Please verify to ensure stable connectivity.`
                            : ''
                    }
                    placement="top"
                    arrow
                >
                    <a
                        href={`http://${camera.IP}`}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                            color:
                                !_.isNil(camera.VendorCameraId) &&
                                solar4gCamera?.Solar4gCellularStatus?.IpAddr != camera.IP
                                    ? color.danger
                                    : color.secondary,
                        }}
                    >
                        {camera.IP}
                    </a>
                </Tooltip>
            ),
        },
        {
            id: 2,
            label: 'Username',
            value: <Typography>{camera.UserName}</Typography>,
        },
        {
            id: 3,
            label: 'Password',
            value: (
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography>{showPassword ? camera.PassWord : camera.PassWord?.replace(/./g, '*')}</Typography>
                    {showPassword ? (
                        <BiHide fontSize={20} color="#85858A" cursor="pointer" onClick={() => setShowPassword(false)} />
                    ) : (
                        <BiShow fontSize={20} color="#85858A" cursor="pointer" onClick={() => setShowPassword(true)} />
                    )}
                </Stack>
            ),
        },
    ];

    const tabSolarConnectivityAndHardware: DetailsInformation[] = [
        {
            id: 1,
            label: 'ISP',
            value: <Typography>{cellularStatus?.Isp}</Typography>,
        },
        {
            id: 2,
            label: 'Signal strength',
            value: (
                <Typography>
                    {checkCellularEnum({ signalStrength: cellularStatus?.SignalStrength }).checkSignalStrength}
                </Typography>
            ),
        },
        {
            id: 3,
            label: 'IMEI',
            value: <Typography>{cellularStatus?.Imei}</Typography>,
        },
        {
            id: 4,
            label: 'ICCID',
            value: <Typography>{cellularStatus?.Iccid}</Typography>,
        },
        {
            id: 5,
            label: 'IMSI',
            value: <Typography>{cellularStatus?.Imsi}</Typography>,
        },
        {
            id: 6,
            label: 'SIM card status',
            value: <Typography>{cellularStatus?.SimStatus}</Typography>,
        },
        {
            id: 7,
            label: 'Status',
            value: <Typography>{cellularStatus?.Imei}</Typography>,
        },
        {
            id: 8,
            label: 'Data usage monthly',
            value: (
                <>
                    {cellularStatus?.DataMonthly && (
                        <Typography>{(cellularStatus?.DataMonthly / 1024).toFixed(2)} Mib</Typography>
                    )}
                </>
            ),
        },
    ];

    const tabSolarCellularSettings: DetailsInformation[] = [
        {
            id: 1,
            label: 'APN',
            value: <Typography>{settings?.Apn}</Typography>,
        },
        {
            id: 2,
            label: 'Username',
            value: <Typography>{settings?.UserName}</Typography>,
        },
        {
            id: 3,
            label: 'Password',
            value: (
                <>
                    {settings?.Password !== null && (
                        <Stack direction="row" spacing={5} alignItems="center" justifyContent="space-between">
                            <Typography>
                                {showPasswordCellularSettings
                                    ? settings?.Password
                                    : settings?.Password?.replace(/./g, '*')}
                            </Typography>
                            {showPasswordCellularSettings ? (
                                <BiHide
                                    fontSize={20}
                                    color="#85858A"
                                    cursor="pointer"
                                    onClick={() => setShowPasswordCellularSettings(false)}
                                />
                            ) : (
                                <BiShow
                                    fontSize={20}
                                    color="#85858A"
                                    cursor="pointer"
                                    onClick={() => setShowPasswordCellularSettings(true)}
                                />
                            )}
                        </Stack>
                    )}
                </>
            ),
        },
        {
            id: 4,
            label: 'Network type',
            value: (
                <Typography>{checkCellularEnum({ networkType: settings?.NetworkType }).checkNetworkType}</Typography>
            ),
        },
        {
            id: 5,
            label: 'Ping detection',
            value: <>{settings?.PingEnable && <Typography>{settings?.PingEnable ? 'Yes' : 'No'}</Typography>}</>,
        },
    ];

    const captureInfo = solar4gCamera?.Solar4gCaptureInfo;

    const tabSolarMQTTPostTypeSettings: DetailsInformation[] = [
        {
            id: 1,
            label: 'Snapshot topic',
            value: <Typography>{captureInfo?.SnapshotTopic}</Typography>,
        },
        {
            id: 2,
            label: 'Status topic',
            value: <Typography>{captureInfo?.StatusTopic}</Typography>,
        },
        {
            id: 3,
            label: 'Alarm topic',
            value: <Typography>{captureInfo?.AlarmTopic}</Typography>,
        },
        {
            id: 4,
            label: 'Configure request topic',
            value: <Typography>{captureInfo?.ConfigTopic}</Typography>,
        },
        {
            id: 5,
            label: 'Configure response topic',
            value: <Typography>{captureInfo?.ConfigRespTopic}</Typography>,
        },
        {
            id: 6,
            label: 'Receive photo URL',
            value: <Typography>{captureInfo?.HttpUrl}</Typography>,
        },
        {
            id: 7,
            label: 'Username',
            value: <Typography>{captureInfo?.UserName}</Typography>,
        },
        {
            id: 8,
            label: 'Password',
            value: (
                <>
                    {captureInfo?.Password !== null && (
                        <Stack direction="row" spacing={5} alignItems="center" justifyContent="space-between">
                            <Typography>
                                {showPasswordMQTT ? captureInfo?.Password : captureInfo?.Password?.replace(/./g, '*')}
                            </Typography>
                            {showPasswordMQTT ? (
                                <BiHide
                                    fontSize={20}
                                    color="#85858A"
                                    cursor="pointer"
                                    onClick={() => setShowPasswordMQTT(false)}
                                />
                            ) : (
                                <BiShow
                                    fontSize={20}
                                    color="#85858A"
                                    cursor="pointer"
                                    onClick={() => setShowPasswordMQTT(true)}
                                />
                            )}
                        </Stack>
                    )}
                </>
            ),
        },
        {
            id: 9,
            label: 'Post type',
            value: (
                <>
                    {captureInfo?.PostEnable !== null && (
                        <Typography>{captureInfo?.PostEnable === 1 ? 'Yes' : 'No'}</Typography>
                    )}
                </>
            ),
        },
    ];

    const tabSolarNetworkConfiguration: DetailsInformation[] = [
        {
            id: 1,
            label: 'IP address',
            value: <Typography>{cellularStatus?.IpAddr}</Typography>,
        },
        {
            id: 2,
            label: 'Gateway',
            value: <Typography>{cellularStatus?.Gateway}</Typography>,
        },
        {
            id: 3,
            label: 'DNS address',
            value: <Typography>{cellularStatus?.Dns}</Typography>,
        },
    ];

    const tabPTZDayNightSwitch: DetailsInformation[] = [
        {
            id: 1,
            label: 'Mode',
            value: (
                <Typography>
                    {irCutFilterMapping[camera.PTZCamera?.PTZCameraConfig?.CameraSetting?.IrCutFilter!]}
                </Typography>
            ),
        },
        {
            id: 2,
            label: 'Day to night value',
            value: <Typography>{camera.PTZCamera?.PTZCameraConfig?.CameraSetting?.DayToNight}</Typography>,
        },
        {
            id: 3,
            label: 'Night to day value',
            value: <Typography>{camera.PTZCamera?.PTZCameraConfig?.CameraSetting?.NightToDay}</Typography>,
        },
        {
            id: 4,
            label: 'Day/Night switch refocus',
            value: (
                <Typography>
                    {dnSwitchRefocusMapping[camera.PTZCamera?.PTZCameraConfig?.CameraSetting?.DnSwitchRefocus!]}
                </Typography>
            ),
        },
    ];

    const tabPTZSmartIRMode: DetailsInformation[] = [
        {
            id: 1,
            label: 'Mode',
            value: (
                <Typography>
                    {smartIrModeMapping[camera.PTZCamera?.PTZCameraConfig?.CameraSetting?.SmartIrMode!]}
                </Typography>
            ),
        },
        {
            id: 2,
            label: 'IR strength value',
            value: (
                <Stack direction="row" spacing={1}>
                    <IrLevelDisplay
                        label="Near"
                        value={
                            camera.PTZCamera?.PTZCameraConfig?.CameraSetting?.SmartIrMode! === SmartIrMode.Customize
                                ? camera.PTZCamera?.PTZCameraConfig?.CameraSetting?.NearViewIrLevel!
                                : 0
                        }
                    />
                    <IrLevelDisplay
                        label="Mid"
                        value={
                            camera.PTZCamera?.PTZCameraConfig?.CameraSetting?.SmartIrMode! === SmartIrMode.Customize
                                ? camera.PTZCamera?.PTZCameraConfig?.CameraSetting?.MiddleViewIrLevel!
                                : 0
                        }
                    />
                    <IrLevelDisplay
                        label="Far"
                        value={
                            camera.PTZCamera?.PTZCameraConfig?.CameraSetting?.SmartIrMode! === SmartIrMode.Customize
                                ? camera.PTZCamera?.PTZCameraConfig?.CameraSetting?.FarViewIrLevel!
                                : 0
                        }
                    />
                </Stack>
            ),
        },
    ];

    const tabPTZExposure: DetailsInformation[] = [
        {
            id: 1,
            label: 'Mode',
            value: (
                <Typography>
                    {exposureModeMapping[camera.PTZCamera?.PTZCameraConfig?.CameraSetting?.ExposureMode!]}
                </Typography>
            ),
        },
        {
            id: 2,
            label: 'Exposure time',
            value: (
                <Typography>
                    {exposureTimeMapping[camera.PTZCamera?.PTZCameraConfig?.CameraSetting?.ExposureTime!]}
                </Typography>
            ),
        },
        {
            id: 3,
            label: 'Gain level',
            value: <Typography>{camera.PTZCamera?.PTZCameraConfig?.CameraSetting?.ExposureGain}</Typography>,
        },
    ];

    const tabPTZSDCard: DetailsInformation[] = [
        {
            id: 1,
            label: 'SD card',
            value: (
                <Typography>{`${camera.PTZCamera?.PTZCameraConfig?.StorageSetting?.SdcardFreeSize!}M / ${camera
                    .PTZCamera?.PTZCameraConfig?.StorageSetting?.SdcardUseSize!}M`}</Typography>
            ),
        },
    ];

    const [buttonSelected, setButtonSelected] = useState<number>(1);
    const [snapshotInfo, setSnapshotInfo] = useState<NotifyItem>({} as NotifyItem);

    useEffect(() => {
        if (
            camera.PTZCamera?.PTZCameraConfig?.SnapShotSetting?.NotifyList &&
            camera.PTZCamera?.PTZCameraConfig?.SnapShotSetting.NotifyList.length > 0
        ) {
            const firstItem = camera.PTZCamera?.PTZCameraConfig?.SnapShotSetting.NotifyList.find(
                (item) => item.Index === 0
            );
            if (firstItem) {
                setSnapshotInfo({
                    Enable: firstItem.Enable,
                    User: firstItem.User,
                    Password: firstItem.Password,
                    Url: firstItem.Url,
                    Index: firstItem.Index,
                });
            }
        }
    }, [camera.PTZCamera?.PTZCameraConfig?.SnapShotSetting?.NotifyList]);

    const handleButtonClick = (index: number) => {
        setButtonSelected(index);

        const selectedData = camera.PTZCamera?.PTZCameraConfig?.SnapShotSetting?.NotifyList?.find(
            (item) => item.Index + 1 === index
        );
        if (selectedData) {
            setSnapshotInfo((prev) => ({
                ...prev,
                Enable: selectedData.Enable,
                User: selectedData.User,
                Password: selectedData.Password,
                Url: selectedData.Url,
            }));
        }
    };

    const tabPTZSnapshotSettings: DetailsInformation[] = [
        {
            id: 1,
            label: 'Enable timing snapshot',
            value: (
                <Typography>
                    {camera.PTZCamera?.PTZCameraConfig?.SnapShotSetting?.TimingSnapshotEnable ? 'Yes' : 'No'}
                </Typography>
            ),
        },
        {
            id: 2,
            label: 'Interval',
            value: (
                <Typography>{`${camera.PTZCamera?.PTZCameraConfig?.SnapShotSetting?.IntervalValue}${
                    intervalUnitMapping[camera.PTZCamera?.PTZCameraConfig?.SnapShotSetting?.IntervalUnit!]
                }`}</Typography>
            ),
        },
        {
            id: 3,
            label: 'Save to storage',
            value: (
                <Typography>
                    {camera.PTZCamera?.PTZCameraConfig?.SnapShotSetting?.SaveIntoStorage ? 'Yes' : 'No'}
                </Typography>
            ),
        },
        {
            id: 4,
            label: 'HTTP post',
            value: (
                <Typography>
                    {camera.PTZCamera?.PTZCameraConfig?.SnapShotSetting?.UploadViaHttp ? 'Enable' : 'Disable'}
                </Typography>
            ),
        },
        {
            id: 5,
            label: 'URL index',
            value: (
                <Stack direction={'row'} spacing={2}>
                    {camera.PTZCamera?.PTZCameraConfig?.SnapShotSetting?.NotifyList?.map((value) => (
                        <Button
                            key={value.Index}
                            variant="contained"
                            size="small"
                            onClick={() => handleButtonClick(value.Index + 1)}
                            sx={{
                                backgroundColor: 'transparent',
                                border:
                                    buttonSelected === value.Index + 1
                                        ? `1px solid ${color.success}`
                                        : `1px solid ${color.grey200}`,
                                minWidth: '50px',
                                '&:hover': {
                                    backgroundColor: buttonSelected === value.Index + 1 ? '#E5F5ED' : color.grey200,
                                    borderColor: buttonSelected === value.Index + 1 ? color.success : color.grey200,
                                },
                            }}
                        >
                            <Typography textAlign={'center'} fontSize={12}>
                                {value.Index + 1}
                            </Typography>
                        </Button>
                    ))}
                </Stack>
            ),
        },
        {
            id: 6,
            label: 'URL',
            value: <Typography>{snapshotInfo.Url}</Typography>,
        },
        {
            id: 7,
            label: 'User name',
            value: <Typography>{snapshotInfo.User}</Typography>,
        },
        {
            id: 8,
            label: 'Password',
            value: (
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography>
                        {showPasswordSnapshotSettings
                            ? snapshotInfo.Password
                            : snapshotInfo.Password?.replace(/./g, '*')}
                    </Typography>
                    {showPasswordSnapshotSettings ? (
                        <BiHide
                            fontSize={20}
                            color="#85858A"
                            cursor="pointer"
                            onClick={() => setShowPasswordSnapshotSettings(false)}
                        />
                    ) : (
                        <BiShow
                            fontSize={20}
                            color="#85858A"
                            cursor="pointer"
                            onClick={() => setShowPasswordSnapshotSettings(true)}
                        />
                    )}
                </Stack>
            ),
        },
    ];

    const tabPTZScheduleSettings: DetailsInformation[] = [
        {
            id: 1,
            label: 'Schedule settings',
            value: (
                <>
                    {transformScheduleData(
                        camera.PTZCamera?.PTZCameraConfig?.SnapShotSetting?.ScheduleList!,
                        (dayName, schedule) => ({
                            label: `${dayName}:`,
                            value: schedule,
                        })
                    )?.map((item, index) => (
                        <Stack direction={'row'} spacing={1} alignItems={'center'} mb={1}>
                            <Typography key={index}>{item.label}</Typography>
                            <Typography key={index}>{item.value}</Typography>
                        </Stack>
                    ))}
                </>
            ),
        },
    ];

    const tabSIMInformation: DetailsInformation[] = [
        {
            id: 1,
            label: 'Network Provider',
            value: <Typography>{camera?.CameraSIM?.NetworkProvider}</Typography>,
        },
        {
            id: 2,
            label: 'Phone Number',
            value: <Typography>{camera?.CameraSIM?.PhoneNumber}</Typography>,
        },
        {
            id: 3,
            label: 'Serial Number',
            value: <Typography>{camera?.CameraSIM?.SerialNumber}</Typography>,
        },
        {
            id: 4,
            label: 'Status',
            value: <Typography>{mappingSIMStatus[camera?.CameraSIM?.Status ?? -1]}</Typography>,
        },
        {
            id: 4,
            label: 'Type',
            value: <Typography>{mappingSIMType[camera?.CameraSIM?.Type ?? -1]}</Typography>,
        },
    ];

    const idsDetailHeaderTop = camera.VendorCameraId === null ? [1, 2] : [1, 2, 3, 4];
    const idsDetailHeaderBot = camera.VendorCameraId === null && camera.PTZCameraId === null ? [1, 2] : [1, 2, 3];

    const filteredDetailHeaderTop = detailHeaderTop.filter((d) => idsDetailHeaderTop.includes(d.id));
    const filteredDetailHeaderBot = detailHeaderBot.filter((d) => idsDetailHeaderBot.includes(d.id));

    const {
        data: notes,
        error,
        isLoading: loading,
    } = useQuery(
        [
            'listNotes',
            camera.Id,
            filter.page,
            debouncedValue,
            filter.to,
            filter.from,
            filter.sortedOrder,
            filter.sort,
            valueTabNotes,
        ],
        handleGetListNote,
        {
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            enabled: !!camera.Id,
        }
    );

    useEffect(() => {
        setIsActive(camera.Status);
        setIsVerification({
            IsVerificationsEnabled: camera.IsVerificationsEnabled!,
            EnablePcnVerificationAt: camera.EnablePcnVerificationAt!,
        });
    }, [camera.IsVerificationsEnabled, camera.Status]);

    useEffect(() => {
        if (camera.PTZCameraId !== null) {
            setCameraDetailsPTZ(camera?.PTZCamera?.PTZCameraConfig?.SysInfo || ({} as PTZSysInfo));
        }

        if (camera.SP111CameraId !== null) {
            handleGetDetailsCameraSP111();
        }

        if (camera.PMC8266CameraId !== null) {
            setCameraDetailsPMC8266(camera.PMC8266Camera?.PMC8266CameraConfigs?.SysInfo as PMC8266SysInfo);
        }
    }, []);
    const tabNotes: TabConfig[] = [
        {
            index: 1,
            label: 'Camera note',
        },
        {
            index: 2,
            label: `Verifier note`,
        },
    ];

    const handleResetFilterNotes = () => {
        setFilter({
            page: 1,
            from: undefined,
            to: undefined,
            search: '',
            sortedOrder: cameraSortByStatus[1],
            sort: cameraNoteSortByType[0],
        });
        setSearchValue('');
    };

    const handleChangeTabNotes = (event: React.SyntheticEvent, newValue: number) => {
        setValueTabNotes(newValue);
        handleResetFilterNotes();
    };

    return (
        <>
            <Stack padding={2} borderRadius={'0px 16px 16px 16px'} border={'1px solid #E4E7EC'}>
                {/* InfoHeader */}
                <Stack width={'100%'} direction={'row'} justifyContent={'space-between'}>
                    <Box width={'100%'}>
                        <Stack direction="row" justifyContent="space-between">
                            <Stack direction={'row'} spacing={1} alignItems={'center'}>
                                <Typography variant="h5">Information camera:</Typography>
                                <Typography variant="h5" color={color.secondary}>
                                    {camera.Name ?? ''}
                                </Typography>
                            </Stack>
                            {!isMobileSM && (
                                <Button
                                    variant="contained"
                                    onClick={() => {
                                        navigate(`/cameras/${camera.Id}/edit`);
                                    }}
                                >
                                    Edit camera
                                </Button>
                            )}
                        </Stack>
                        {isMediumMD ? (
                            <Stack spacing={0.5} mb={!camera.PTZCameraId ? 1 : 0}>
                                <Stack direction="row" spacing={1}>
                                    {filteredDetailHeaderTop.slice(0, 2).map((item, index) => (
                                        <Box key={index} display="flex" alignItems="center">
                                            <Stack direction={'row'} spacing={1} alignItems={'center'}>
                                                <Typography variant="body1" color={'#85858A'}>
                                                    {item.label}:
                                                </Typography>
                                                <Typography variant="body1" color={'#85858A'}>
                                                    {item.value}
                                                </Typography>
                                            </Stack>
                                            {index < filteredDetailHeaderTop.slice(0, 2).length - 1 && (
                                                <Divider
                                                    orientation="vertical"
                                                    flexItem
                                                    sx={{
                                                        borderRightWidth: 1,
                                                        borderColor: '#85858A',
                                                        px: '4px',
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    ))}
                                </Stack>
                                <Stack direction="row" spacing={1}>
                                    {filteredDetailHeaderTop.slice(2).map((item, index) => (
                                        <Box key={index} display="flex" alignItems="center">
                                            <Stack direction={'row'} spacing={1} alignItems={'center'}>
                                                <Typography variant="body1" color={'#85858A'}>
                                                    {item.label}:
                                                </Typography>
                                                <Typography variant="body1" color={'#85858A'}>
                                                    {item.value}
                                                </Typography>
                                            </Stack>
                                            {index < filteredDetailHeaderTop.slice(2).length - 1 && (
                                                <Divider
                                                    orientation="vertical"
                                                    flexItem
                                                    sx={{
                                                        borderRightWidth: 1,
                                                        borderColor: '#85858A',
                                                        px: '4px',
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    ))}
                                </Stack>
                            </Stack>
                        ) : (
                            <Stack direction={'row'} spacing={1} alignItems={'center'} my={1}>
                                {filteredDetailHeaderTop.map((item, index) => (
                                    <Box key={index} display="flex" alignItems="center">
                                        <Stack direction={'row'} spacing={1} alignItems={'center'}>
                                            <Typography variant="body1" color={'#85858A'}>
                                                {item.label}:
                                            </Typography>
                                            <Typography variant="body1" color={'#85858A'}>
                                                {item.value}
                                            </Typography>
                                        </Stack>
                                        {index < filteredDetailHeaderTop.length - 1 && (
                                            <Divider
                                                orientation="vertical"
                                                flexItem
                                                sx={{
                                                    borderRightWidth: 1,
                                                    borderColor: '#85858A',
                                                    px: '4px',
                                                }}
                                            />
                                        )}
                                    </Box>
                                ))}
                            </Stack>
                        )}

                        <Stack
                            direction={isMobileSM ? 'column' : 'row'}
                            spacing={1}
                            alignItems={isMobileSM ? 'flex-start' : 'center'}
                        >
                            {filteredDetailHeaderBot.map((item, index) => (
                                <Box key={index} display="flex" alignItems="center">
                                    <Stack direction={'row'} spacing={1} alignItems={'center'}>
                                        <Typography variant="body1" color={'#85858A'}>
                                            {item.label}:
                                        </Typography>
                                        <Typography variant="body1" color={'#85858A'}>
                                            {item.value}
                                        </Typography>
                                    </Stack>
                                    {index < filteredDetailHeaderBot.length - 1 && !isMobileSM && (
                                        <Divider
                                            orientation="vertical"
                                            flexItem
                                            sx={{
                                                borderRightWidth: 1,
                                                borderColor: '#85858A',
                                                px: '4px',
                                            }}
                                        />
                                    )}
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                </Stack>

                <Grid
                    container
                    xs={12}
                    bgcolor={color.grey100}
                    p={'16px 16px 0px 16px'}
                    borderRadius={'8px 8px 0px 0px'}
                    mt={2}
                    justifyContent="space-between"
                >
                    <Grid
                        item
                        container
                        xs={isMediumMD ? 12 : 5.9}
                        height={'100%'}
                        gap={2}
                        justifyContent={'space-between'}
                    >
                        {/* Details */}
                        <BaseComponentDetails
                            title="Details"
                            styleTitle={{
                                mb: 2,
                            }}
                            content={
                                <Grid item container xs={12} spacing={2}>
                                    {camera.PTZCameraId !== null ? (
                                        <>
                                            {tabDetailsCameraPTZ.map((item, index) => (
                                                <Grid item container key={index} xs={12}>
                                                    <Grid item xs={5}>
                                                        <Typography variant="body1">{item.label}:</Typography>
                                                    </Grid>
                                                    <Grid item xs={7}>
                                                        <Typography variant="body1">{item.value}</Typography>
                                                    </Grid>
                                                </Grid>
                                            ))}
                                        </>
                                    ) : camera.VendorCameraId !== null ? (
                                        <>
                                            {tabDetailsCameraSolar
                                                .filter((item) => !Boolean(item.isHide))
                                                .map((item, index) => (
                                                    <Grid item container key={index} xs={12}>
                                                        <Grid item xs={5}>
                                                            <Typography variant="body1">{item.label}:</Typography>
                                                        </Grid>
                                                        <Grid item xs={7}>
                                                            <Typography variant="body1">{item.value}</Typography>
                                                        </Grid>
                                                    </Grid>
                                                ))}
                                        </>
                                    ) : camera.SP111CameraId !== null ? (
                                        <>
                                            {tabDetailsCameraSP111.map((item, index) => (
                                                <Grid item container key={index} xs={12}>
                                                    <Grid item xs={5}>
                                                        <Typography variant="body1">{item.label}:</Typography>
                                                    </Grid>
                                                    <Grid item xs={7}>
                                                        <Typography variant="body1">{item.value}</Typography>
                                                    </Grid>
                                                </Grid>
                                            ))}
                                        </>
                                    ) : (
                                        <>
                                            {tabDetailsCameraPMC8266.map((item, index) => (
                                                <Grid item container key={index} xs={12}>
                                                    <Grid item xs={5}>
                                                        <Typography variant="body1">{item.label}:</Typography>
                                                    </Grid>
                                                    <Grid item xs={7}>
                                                        <Typography variant="body1">{item.value}</Typography>
                                                    </Grid>
                                                </Grid>
                                            ))}
                                        </>
                                    )}
                                </Grid>
                            }
                        />

                        {/* Production */}
                        <BaseComponentDetails
                            title="Production"
                            styleTitle={{
                                mb: 2,
                            }}
                            content={
                                <Grid item container xs={12} spacing={2}>
                                    <Grid item container xs={12}>
                                        <Grid item xs={5}>
                                            <Typography variant="body1">Production:</Typography>
                                        </Grid>
                                        <Grid item xs={7}>
                                            <Typography variant="body1">
                                                {formatUKDate(camera.DeployedDate)}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            }
                        />

                        {(camera?.PTZCameraId || camera?.VendorCameraId) && (
                            <BaseComponentDetails
                                title="SIM"
                                styleTitle={{
                                    mb: 2,
                                }}
                                content={
                                    camera?.CameraSIM ? (
                                        <Grid item container xs={12} spacing={2}>
                                            {tabSIMInformation.map((item, index) => (
                                                <Grid key={index} item container xs={12}>
                                                    <Grid item xs={5}>
                                                        <Typography variant="body1">{item.label}:</Typography>
                                                    </Grid>
                                                    <Grid item xs={7}>
                                                        {item.value}
                                                    </Grid>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    ) : (
                                        <NoDataCameras
                                            sx={{ borderRadius: '0px 0px 4px 4px', minHeight: 150, border: 'none' }}
                                        />
                                    )
                                }
                            />
                        )}

                        {/* Service */}
                        {camera.VendorCameraId !== null && (
                            <BaseComponentDetails
                                title="Service"
                                content={
                                    <Stack mt={2} spacing={2} width={'100%'}>
                                        <Grid xs={12} container>
                                            <Grid xs={5}>
                                                <Typography>Type service:</Typography>
                                            </Grid>
                                            <Grid xs={7}>
                                                <Typography>{camServiceTypes?.[camera.ServiceType]?.label}</Typography>
                                            </Grid>
                                        </Grid>
                                        {camera.ANPRCameraId && (
                                            <Grid xs={12} container>
                                                <Grid xs={5}>
                                                    <Typography>ANPR ID:</Typography>
                                                </Grid>
                                                <Grid xs={7}>
                                                    <Typography>{camera.ANPRCameraId}</Typography>
                                                </Grid>
                                            </Grid>
                                        )}
                                    </Stack>
                                }
                            />
                        )}

                        {camera.VendorCameraId !== null && (
                            <BaseComponentDetails
                                title="Connectivity & Hardware"
                                styleTitle={{
                                    mb: 2,
                                }}
                                content={
                                    <Grid item container xs={12} spacing={2}>
                                        {tabSolarConnectivityAndHardware.map((item, index) => (
                                            <Grid item container key={index} xs={12}>
                                                <Grid item xs={5}>
                                                    <Typography variant="body1">{item.label}:</Typography>
                                                </Grid>
                                                <Grid item xs={7}>
                                                    <Typography variant="body1">{item.value}</Typography>
                                                </Grid>
                                            </Grid>
                                        ))}
                                    </Grid>
                                }
                            />
                        )}

                        {camera.VendorCameraId !== null && (
                            <BaseComponentDetails
                                title="Cellular settings"
                                styleTitle={{
                                    mb: 2,
                                }}
                                content={
                                    <Grid item container xs={12} spacing={2}>
                                        {tabSolarCellularSettings.map((item, index) => (
                                            <Grid item container key={index} xs={12}>
                                                <Grid item xs={5}>
                                                    <Typography variant="body1">{item.label}:</Typography>
                                                </Grid>
                                                <Grid item xs={7}>
                                                    <Typography variant="body1">{item.value}</Typography>
                                                </Grid>
                                            </Grid>
                                        ))}
                                    </Grid>
                                }
                            />
                        )}

                        {camera.PTZCameraId !== null && (
                            <BaseComponentDetails
                                title="Day/Night switch"
                                styleTitle={{
                                    mb: 2,
                                }}
                                content={
                                    <Grid item container xs={12} spacing={2}>
                                        {tabPTZDayNightSwitch.map((item, index) => (
                                            <Grid item container key={index} xs={12}>
                                                <Grid item xs={5}>
                                                    <Typography variant="body1">{item.label}:</Typography>
                                                </Grid>
                                                <Grid item xs={7}>
                                                    <Typography variant="body1">{item.value}</Typography>
                                                </Grid>
                                            </Grid>
                                        ))}
                                    </Grid>
                                }
                            />
                        )}

                        {camera.PTZCameraId !== null && (
                            <BaseComponentDetails
                                title="SD card"
                                styleTitle={{
                                    mb: 2,
                                }}
                                content={
                                    <Grid item container xs={12} spacing={2}>
                                        {tabPTZSDCard.map((item, index) => (
                                            <Grid item container key={index} xs={12}>
                                                <Grid item xs={5}>
                                                    <Typography variant="body1">{item.label}:</Typography>
                                                </Grid>
                                                <Grid item xs={7}>
                                                    <Typography variant="body1">{item.value}</Typography>
                                                </Grid>
                                            </Grid>
                                        ))}
                                    </Grid>
                                }
                            />
                        )}

                        {camera.PTZCameraId !== null && (
                            <BaseComponentDetails
                                title="Snapshot settings"
                                styleTitle={{
                                    mb: 2,
                                }}
                                content={
                                    <Grid item container xs={12} spacing={2}>
                                        {tabPTZSnapshotSettings.map((item, index) => (
                                            <Grid item container key={index} xs={12}>
                                                <Grid item xs={5}>
                                                    <Typography variant="body1">{item.label}:</Typography>
                                                </Grid>
                                                <Grid item xs={7}>
                                                    <Typography variant="body1">{item.value}</Typography>
                                                </Grid>
                                            </Grid>
                                        ))}
                                    </Grid>
                                }
                            />
                        )}
                    </Grid>

                    <Grid
                        item
                        container
                        xs={isMediumMD ? 12 : 5.9}
                        height={'100%'}
                        gap={2}
                        justifyContent={'space-between'}
                    >
                        {/* Allocated zone */}
                        <BaseComponentDetails
                            title="Allocated zone"
                            styleTitle={{
                                mb: 2,
                            }}
                            content={
                                <Grid item container xs={12} spacing={2}>
                                    {tabAllocatedZone.map((item, index) => (
                                        <Grid item container key={index} xs={12}>
                                            <Grid item xs={5}>
                                                <Typography variant="body1">{item.label}:</Typography>
                                            </Grid>
                                            <Grid item xs={7}>
                                                <Typography variant="body1">{item.value}</Typography>
                                            </Grid>
                                        </Grid>
                                    ))}
                                </Grid>
                            }
                        />

                        {camera.ExtendedCoverage && camera.ExtendedCoverage?.length > 0 && (
                            <BaseComponentDetails
                                title="Camera observed zones"
                                styleTitle={{
                                    mb: 2,
                                }}
                                content={
                                    <Grid item container xs={12} spacing={2}>
                                        {tabCrossZoneCoverage.map((item, index) => (
                                            <Grid item container key={index} xs={12}>
                                                <Grid item xs={5}>
                                                    <Typography variant="body1">{item.label}:</Typography>
                                                </Grid>
                                                <Grid item xs={7}>
                                                    {item.value}
                                                </Grid>
                                            </Grid>
                                        ))}
                                    </Grid>
                                }
                            />
                        )}

                        {/* Data processing */}
                        <BaseComponentDetails
                            title="Data processing"
                            styleTitle={{
                                mb: 2,
                            }}
                            content={
                                <Grid item container xs={12} spacing={2}>
                                    {tabDataProcessing.map((item, index) => (
                                        <Grid item container key={index} xs={12}>
                                            <Grid item xs={5}>
                                                <Typography variant="body1">{item.label}:</Typography>
                                            </Grid>
                                            <Grid item xs={7}>
                                                <Typography variant="body1">{item.value}</Typography>
                                            </Grid>
                                        </Grid>
                                    ))}
                                </Grid>
                            }
                        />

                        {/* EV permit exemption */}
                        <EvPermitExemptionSummary camera={camera} />

                        {/* Camera login information */}
                        <BaseComponentDetails
                            title="Camera login information"
                            styleTitle={{
                                mb: 2,
                            }}
                            content={
                                <Grid item container xs={12} spacing={2}>
                                    {tabLoginInformation.map((item, index) => (
                                        <Grid item container key={index} xs={12}>
                                            <Grid item xs={5}>
                                                <Typography
                                                    color={getHighlightColor(
                                                        item.label === 'IP' &&
                                                            !_.isNil(camera.VendorCameraId) &&
                                                            solar4gCamera?.Solar4gCellularStatus?.IpAddr !== camera.IP
                                                    )}
                                                    variant="body1"
                                                >
                                                    {item.label}:
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={7}>
                                                <Typography variant="body1">{item.value}</Typography>
                                            </Grid>
                                        </Grid>
                                    ))}
                                </Grid>
                            }
                        />

                        {/* Service */}
                        {camera.VendorCameraId === null && (
                            <BaseComponentDetails
                                title="Service"
                                // isHideAction={isHideAction}
                                // iconAdd={<IcEdit style={{ width: '14px', height: '14px' }} />}
                                // titleTooltipIconAdd="Edit service"
                                // popupAdd={popUpEditService}
                                content={
                                    <Stack mt={2} spacing={2} width={'100%'}>
                                        <Grid xs={12} container>
                                            <Grid xs={5}>
                                                <Typography>Type service:</Typography>
                                            </Grid>
                                            <Grid xs={7}>
                                                <Typography>{camServiceTypes[camera.ServiceType]?.label}</Typography>
                                            </Grid>
                                        </Grid>
                                        {camera.ANPRCameraId && (
                                            <Grid xs={12} container>
                                                <Grid xs={5}>
                                                    <Typography>ANPR ID:</Typography>
                                                </Grid>
                                                <Grid xs={7}>
                                                    <Typography>{camera.ANPRCameraId}</Typography>
                                                </Grid>
                                            </Grid>
                                        )}
                                    </Stack>
                                }
                            />
                        )}

                        {camera.VendorCameraId !== null && (
                            <BaseComponentDetails
                                title="MQTT / Post type settings"
                                styleTitle={{
                                    mb: 2,
                                }}
                                content={
                                    <Grid item container xs={12} spacing={2}>
                                        {tabSolarMQTTPostTypeSettings.map((item, index) => (
                                            <Grid item container key={index} xs={12}>
                                                <Grid item xs={5}>
                                                    <Typography variant="body1">{item.label}:</Typography>
                                                </Grid>
                                                <Grid item xs={7}>
                                                    <Typography variant="body1">{item.value}</Typography>
                                                </Grid>
                                            </Grid>
                                        ))}
                                    </Grid>
                                }
                            />
                        )}

                        {camera.VendorCameraId !== null && (
                            <BaseComponentDetails
                                title="Network configuration"
                                styleTitle={{
                                    mb: 2,
                                }}
                                content={
                                    <Grid item container xs={12} spacing={2}>
                                        {tabSolarNetworkConfiguration.map((item, index) => (
                                            <Grid item container key={index} xs={12}>
                                                <Grid item xs={5}>
                                                    <Typography variant="body1">{item.label}:</Typography>
                                                </Grid>
                                                <Grid item xs={7}>
                                                    <Typography variant="body1">{item.value}</Typography>
                                                </Grid>
                                            </Grid>
                                        ))}
                                    </Grid>
                                }
                            />
                        )}

                        {camera.PTZCameraId !== null && (
                            <BaseComponentDetails
                                title="Exposure"
                                styleTitle={{
                                    mb: 2,
                                }}
                                content={
                                    <Grid item container xs={12} spacing={2}>
                                        {tabPTZExposure.map((item, index) => (
                                            <Grid item container key={index} xs={12}>
                                                <Grid item xs={5}>
                                                    <Typography variant="body1">{item.label}:</Typography>
                                                </Grid>
                                                <Grid item xs={7}>
                                                    <Typography variant="body1">{item.value}</Typography>
                                                </Grid>
                                            </Grid>
                                        ))}
                                    </Grid>
                                }
                            />
                        )}

                        {camera.PTZCameraId !== null && (
                            <BaseComponentDetails
                                title="Smart IR mode"
                                styleTitle={{
                                    mb: 2,
                                }}
                                content={
                                    <Grid item container xs={12} spacing={2}>
                                        {tabPTZSmartIRMode.map((item, index) => (
                                            <Grid item container key={index} xs={12}>
                                                <Grid item xs={5}>
                                                    <Typography variant="body1">{item.label}:</Typography>
                                                </Grid>
                                                <Grid item xs={7}>
                                                    <Typography variant="body1">{item.value}</Typography>
                                                </Grid>
                                            </Grid>
                                        ))}
                                    </Grid>
                                }
                            />
                        )}

                        {camera.PTZCameraId !== null && (
                            <BaseComponentDetails
                                title="Schedule settings"
                                styleTitle={{
                                    mb: 2,
                                }}
                                content={
                                    <Grid item container xs={12} spacing={2}>
                                        {tabPTZScheduleSettings.map((item, index) => (
                                            <Grid item container key={index} xs={12}>
                                                <Grid item xs={5}>
                                                    <Typography variant="body1">{item.label}:</Typography>
                                                </Grid>
                                                <Grid item xs={7}>
                                                    <Typography variant="body1">{item.value}</Typography>
                                                </Grid>
                                            </Grid>
                                        ))}
                                    </Grid>
                                }
                            />
                        )}
                    </Grid>

                    {/* Recent exemption periods */}
                    <BaseComponentDetails
                        title="Recent exemption periods"
                        style={{ mt: 1 }}
                        isHideAction={isHideAction}
                        iconAdd={<MdAdd style={{ width: '16px', height: '16px' }} />}
                        popupAdd={popUpAddExemption}
                        titleTooltipIconAdd="Add exemption"
                        iconHistory={<TbBook2 style={{ width: '16px', height: '16px' }} />}
                        titleTooltipIconHistory="History exemption"
                        popupHistory={popupHistoryExemption}
                        setMode={setMode}
                        content={
                            <>
                                {!!camera.ExemptionPeriods?.length && (
                                    <Stack mt={2} spacing={2} width={'100%'} paddingX={2}>
                                        <>
                                            <Grid xs={12} container borderBottom={`1px solid ${color.grey200}`} pb={1}>
                                                <Grid xs={7.5}>
                                                    <Typography fontWeight={500}>Effective</Typography>
                                                </Grid>
                                                <Grid xs={3.5}>
                                                    <Typography fontWeight={500}>Description</Typography>
                                                </Grid>
                                            </Grid>
                                            {camera.ExemptionPeriods?.map((item) => {
                                                const isPast = moment(item.EffectiveFrom).tz(appConfig.timeZone).isBefore(
                                                    moment().tz(appConfig.timeZone).startOf('date')
                                                );
                                                return (
                                                    <Grid
                                                        xs={12}
                                                        container
                                                        borderBottom={`1px solid ${color.grey200}`}
                                                        pb={1}
                                                    >
                                                        <Grid xs={7.5}>
                                                            <Stack>
                                                                <Typography>
                                                                    {formatDateByTimeZone(item.EffectiveFrom, undefined, false)}{' '}
                                                                    -{' '}
                                                                    {formatDateByTimeZone(item.EffectiveTo, undefined, false)}
                                                                </Typography>
                                                                <Typography
                                                                    fontSize={12}
                                                                    sx={{ color: (theme) => theme.palette.grey[700] }}
                                                                >
                                                                    By {item.CreatedBy}
                                                                </Typography>
                                                            </Stack>
                                                        </Grid>
                                                        <Grid xs={4.5}>
                                                            <Stack
                                                                direction="row"
                                                                justifyContent="space-between"
                                                                alignItems={'center'}
                                                            >
                                                                <Typography>{item.Notes}</Typography>
                                                                {isPast ? (
                                                                    <></>
                                                                ) : (
                                                                    <Stack
                                                                        direction="row"
                                                                        justifyContent="flex-end"
                                                                        spacing={0.7}
                                                                    >
                                                                        <Box
                                                                            sx={{
                                                                                color: '#85858A',
                                                                                transition: '.2s ease-in-out',
                                                                                cursor: 'pointer',
                                                                                '&:hover': {
                                                                                    color: (theme) =>
                                                                                        theme.palette.secondary.main,
                                                                                },
                                                                            }}
                                                                            onClick={() => {
                                                                                setMode(Mode.edit);
                                                                                setSelectedExemption(item);
                                                                                popUpAddExemption.setTrue();
                                                                            }}
                                                                        >
                                                                            <IcEdit />
                                                                        </Box>
                                                                        <Box
                                                                            sx={{
                                                                                cursor: 'pointer',
                                                                            }}
                                                                            onClick={() => {
                                                                                setSelectedExemption(item);
                                                                                popUpConfirmDelete.setTrue();
                                                                            }}
                                                                        >
                                                                            <ICDelete fontSize={18} />
                                                                        </Box>
                                                                    </Stack>
                                                                )}
                                                            </Stack>
                                                        </Grid>
                                                    </Grid>
                                                );
                                            })}
                                        </>
                                    </Stack>
                                )}
                                {!camera.ExemptionPeriods?.length && (
                                    <NoDataCameras
                                        sx={{ borderRadius: '0px 0px 4px 4px', minHeight: 150, border: 'none' }}
                                    />
                                )}
                            </>
                        }
                    />

                    <Box width={'100%'} mt={2}>
                        <BaseEnhancedTabs
                            tabs={tabNotes}
                            value={valueTabNotes}
                            onChange={handleChangeTabNotes}
                            minWidth={'10%'}
                        />

                        {[
                            {
                                name: 'Camera notes',
                                children: (
                                    <BaseComponentDetails
                                        title="Camera notes"
                                        isHideAction={isHideAction}
                                        iconAdd={<MdAdd style={{ width: '16px', height: '16px' }} />}
                                        popupAdd={popupNoteHistory}
                                        titleTooltipIconAdd="Add note"
                                        setMode={setMode}
                                        content={
                                            <Stack mt={2} spacing={2} width={'100%'}>
                                                <Stack mt={2}>
                                                    <FilterRoot
                                                        handleResetFilter={handleResetFilterNotes}
                                                        rightTitleContent={
                                                            <Grid
                                                                container
                                                                xs={9}
                                                                spacing={1}
                                                                justifyContent={'flex-end'}
                                                            >
                                                                <Grid xs={5}>
                                                                    <SearchTypeSelect
                                                                        startAdornment={
                                                                            <Stack direction={'row'}>
                                                                                <SearchTypeSelect
                                                                                    listData={cameraSortByStatus}
                                                                                    value={filter.sortedOrder!}
                                                                                    keyEqual="Id"
                                                                                    keyLabel="Name"
                                                                                    handleChange={(e) => {
                                                                                        setFilter((prev) => ({
                                                                                            ...prev,
                                                                                            sortedOrder: e,
                                                                                            page: 1,
                                                                                        }));
                                                                                    }}
                                                                                    label="Sort by"
                                                                                    minWidth={60}
                                                                                />
                                                                                <Divider
                                                                                    orientation="vertical"
                                                                                    flexItem
                                                                                    sx={{
                                                                                        mx: '5px',
                                                                                        borderRightWidth: 1,
                                                                                        borderColor: color.grey600,
                                                                                    }}
                                                                                    variant="middle"
                                                                                />
                                                                            </Stack>
                                                                        }
                                                                        listData={cameraNoteSortByType}
                                                                        value={filter.sort!}
                                                                        keyEqual="Id"
                                                                        keyLabel="Name"
                                                                        handleChange={(e) => {
                                                                            setFilter((prev) => ({
                                                                                ...prev,
                                                                                sort: e,
                                                                                page: 1,
                                                                            }));
                                                                        }}
                                                                        label="Sort by"
                                                                        minWidth={60}
                                                                    />
                                                                </Grid>
                                                            </Grid>
                                                        }
                                                        filterChild={
                                                            <Grid container xs={12} gap={1} justifyContent={'center'}>
                                                                <Grid xs={5.9}>
                                                                    <BaseTextField
                                                                        placeholder={'Search by email'}
                                                                        value={searchValue}
                                                                        onChange={(e) => {
                                                                            setFilter({
                                                                                page: 1,
                                                                                from: filter.from,
                                                                                to: filter.to,
                                                                                sortedOrder: filter.sortedOrder,
                                                                                sort: filter.sort,
                                                                            });
                                                                            setSearchValue(e.target.value);
                                                                        }}
                                                                        maxLength={50}
                                                                        InputProps={{
                                                                            endAdornment: (
                                                                                <FiSearch
                                                                                    color="disabled"
                                                                                    size={16}
                                                                                    style={{
                                                                                        color: 'rgba(0, 0, 0, 0.38)',
                                                                                        cursor: 'pointer',
                                                                                    }}
                                                                                />
                                                                            ),
                                                                        }}
                                                                    />
                                                                </Grid>
                                                                <Grid xs={5.9}>
                                                                    <StyledDateRangePicker
                                                                        value={{
                                                                            startDate: filter?.from || undefined,
                                                                            endDate: filter?.to || undefined,
                                                                        }}
                                                                        onChange={(value) => {
                                                                            setFilter((prev) => ({
                                                                                ...prev,
                                                                                page: 1,
                                                                                from: value.startDate,
                                                                                to: value.endDate,
                                                                            }));
                                                                        }}
                                                                        dateReset={{
                                                                            startDate: undefined,
                                                                            endDate: undefined,
                                                                        }}
                                                                        label={'Select date'}
                                                                    />
                                                                </Grid>
                                                            </Grid>
                                                        }
                                                    />
                                                </Stack>

                                                <BaseListNote
                                                    paging={notes ?? ({} as Paging<CameraNotes>)}
                                                    isLoading={loading}
                                                    tableHeaders={[...tableHeaders, '']}
                                                    styleContainer={{ mt: 2, mb: 5 }}
                                                    setPage={(page) => {
                                                        setFilter((prev) => ({ ...prev, page }));
                                                    }}
                                                    renderedItem={(item) => {
                                                        return (
                                                            <>
                                                                <CellOfRow
                                                                    value={formatDateTime(item.Created!)}
                                                                    align="left"
                                                                />
                                                                <CellOfRow value={item.CreatedBy ?? ''} align="left" />
                                                                <CellOfRow
                                                                    value={formatDateTime(item.Updated!)}
                                                                    align="left"
                                                                />
                                                                <TableCell
                                                                    align="left"
                                                                    sx={{
                                                                        wordWrap: 'break-word',
                                                                        whiteSpace: 'pre-wrap',
                                                                        wordBreak: 'break-word',
                                                                    }}
                                                                >
                                                                    <Typography>{item.Notes.trim() ?? ''}</Typography>
                                                                </TableCell>
                                                                <CellOfRow
                                                                    value={
                                                                        <>
                                                                            <Stack
                                                                                direction={'row'}
                                                                                spacing={0.7}
                                                                                alignItems={'center'}
                                                                            >
                                                                                <Tooltip placement="top" title="Edit">
                                                                                    <Box
                                                                                        sx={{
                                                                                            color: '#85858A',
                                                                                            transition:
                                                                                                '.2s ease-in-out',
                                                                                            cursor: 'pointer',
                                                                                            '&:hover': {
                                                                                                color: (theme) =>
                                                                                                    theme.palette
                                                                                                        .secondary.main,
                                                                                            },
                                                                                        }}
                                                                                        onClick={() => {
                                                                                            setMode(Mode.edit);
                                                                                            setIdNote(item.Id ?? 0);
                                                                                            popupNoteHistory.setTrue();
                                                                                        }}
                                                                                    >
                                                                                        <IcEdit />
                                                                                    </Box>
                                                                                </Tooltip>
                                                                                <DangerTooltip
                                                                                    placement="top"
                                                                                    title="Delete"
                                                                                >
                                                                                    <Box
                                                                                        sx={{
                                                                                            cursor: 'pointer',
                                                                                        }}
                                                                                        onClick={() => {
                                                                                            setIdNote(item.Id ?? 0);
                                                                                            popUpConfirmDeleteNoteHistory.setTrue();
                                                                                        }}
                                                                                    >
                                                                                        <ICDelete fontSize={18} />
                                                                                    </Box>
                                                                                </DangerTooltip>
                                                                            </Stack>
                                                                        </>
                                                                    }
                                                                    align="left"
                                                                />
                                                            </>
                                                        );
                                                    }}
                                                />
                                            </Stack>
                                        }
                                    />
                                ),
                            },
                            {
                                name: 'Verifier note',
                                children: (
                                    <BaseComponentDetails
                                        title="Verifier note"
                                        isHideAction={isHideAction}
                                        iconAdd={<MdAdd style={{ width: '16px', height: '16px' }} />}
                                        popupAdd={popupNoteHistory}
                                        titleTooltipIconAdd="Add note"
                                        setMode={setMode}
                                        content={
                                            <Stack mt={2} spacing={2} width={'100%'}>
                                                <Stack mt={2}>
                                                    <FilterRoot
                                                        handleResetFilter={handleResetFilterNotes}
                                                        rightTitleContent={
                                                            <Grid
                                                                container
                                                                xs={9}
                                                                spacing={1}
                                                                justifyContent={'flex-end'}
                                                            >
                                                                <Grid xs={5}>
                                                                    <SearchTypeSelect
                                                                        startAdornment={
                                                                            <Stack direction={'row'}>
                                                                                <SearchTypeSelect
                                                                                    listData={cameraSortByStatus}
                                                                                    value={filter.sortedOrder!}
                                                                                    keyEqual="Id"
                                                                                    keyLabel="Name"
                                                                                    handleChange={(e) => {
                                                                                        setFilter((prev) => ({
                                                                                            ...prev,
                                                                                            sortedOrder: e,
                                                                                            page: 1,
                                                                                        }));
                                                                                    }}
                                                                                    label="Sort by"
                                                                                    minWidth={60}
                                                                                />
                                                                                <Divider
                                                                                    orientation="vertical"
                                                                                    flexItem
                                                                                    sx={{
                                                                                        mx: '5px',
                                                                                        borderRightWidth: 1,
                                                                                        borderColor: color.grey600,
                                                                                    }}
                                                                                    variant="middle"
                                                                                />
                                                                            </Stack>
                                                                        }
                                                                        listData={cameraNoteSortByType}
                                                                        value={filter.sort!}
                                                                        keyEqual="Id"
                                                                        keyLabel="Name"
                                                                        handleChange={(e) => {
                                                                            setFilter((prev) => ({
                                                                                ...prev,
                                                                                sort: e,
                                                                                page: 1,
                                                                            }));
                                                                        }}
                                                                        label="Sort by"
                                                                        minWidth={60}
                                                                    />
                                                                </Grid>
                                                            </Grid>
                                                        }
                                                        filterChild={
                                                            <Grid container xs={12} gap={1} justifyContent={'center'}>
                                                                <Grid xs={5.9}>
                                                                    <BaseTextField
                                                                        placeholder={'Search by email'}
                                                                        value={searchValue}
                                                                        onChange={(e) => {
                                                                            setFilter({
                                                                                page: 1,
                                                                                from: filter.from,
                                                                                to: filter.to,
                                                                                sortedOrder: filter.sortedOrder,
                                                                                sort: filter.sort,
                                                                            });
                                                                            setSearchValue(e.target.value);
                                                                        }}
                                                                        maxLength={50}
                                                                        InputProps={{
                                                                            endAdornment: (
                                                                                <FiSearch
                                                                                    color="disabled"
                                                                                    size={16}
                                                                                    style={{
                                                                                        color: 'rgba(0, 0, 0, 0.38)',
                                                                                        cursor: 'pointer',
                                                                                    }}
                                                                                />
                                                                            ),
                                                                        }}
                                                                    />
                                                                </Grid>
                                                                <Grid xs={5.9}>
                                                                    <StyledDateRangePicker
                                                                        value={{
                                                                            startDate: filter?.from || undefined,
                                                                            endDate: filter?.to || undefined,
                                                                        }}
                                                                        onChange={(value) => {
                                                                            setFilter((prev) => ({
                                                                                ...prev,
                                                                                page: 1,
                                                                                from: value.startDate,
                                                                                to: value.endDate,
                                                                            }));
                                                                        }}
                                                                        dateReset={{
                                                                            startDate: undefined,
                                                                            endDate: undefined,
                                                                        }}
                                                                        label={'Select date'}
                                                                    />
                                                                </Grid>
                                                            </Grid>
                                                        }
                                                    />
                                                </Stack>

                                                <BaseListNote
                                                    paging={notes ?? ({} as Paging<CameraNotes>)}
                                                    isLoading={loading}
                                                    tableHeaders={[...tableHeaders, '']}
                                                    styleContainer={{ mt: 2, mb: 5 }}
                                                    setPage={(page) => {
                                                        setFilter((prev) => ({ ...prev, page }));
                                                    }}
                                                    renderedItem={(item) => {
                                                        return (
                                                            <>
                                                                <CellOfRow
                                                                    value={formatDateTime(item.Created!)}
                                                                    align="left"
                                                                />
                                                                <CellOfRow value={item.CreatedBy ?? ''} align="left" />
                                                                <CellOfRow
                                                                    value={formatDateTime(item.Updated!)}
                                                                    align="left"
                                                                />
                                                                <TableCell
                                                                    align="left"
                                                                    sx={{
                                                                        wordWrap: 'break-word',
                                                                        whiteSpace: 'pre-wrap',
                                                                        wordBreak: 'break-word',
                                                                    }}
                                                                >
                                                                    <Typography>{item.Notes.trim() ?? ''}</Typography>
                                                                    {!!item.ExemptVehicleTypes?.length && (
                                                                        <Typography
                                                                            variant="caption"
                                                                            sx={{
                                                                                display: 'block',
                                                                                mt: 0.5,
                                                                                color: color.grey600,
                                                                            }}
                                                                        >
                                                                            Exempt vehicle types:{' '}
                                                                            {item.ExemptVehicleTypes.map(
                                                                                (t) => exemptVehicleTypeReadableMapping[t]
                                                                            ).join(', ')}
                                                                        </Typography>
                                                                    )}
                                                                </TableCell>
                                                                <CellOfRow
                                                                    value={
                                                                        <>
                                                                            <Stack
                                                                                direction={'row'}
                                                                                spacing={0.7}
                                                                                alignItems={'center'}
                                                                            >
                                                                                <Tooltip placement="top" title="Edit">
                                                                                    <Box
                                                                                        sx={{
                                                                                            color: '#85858A',
                                                                                            transition:
                                                                                                '.2s ease-in-out',
                                                                                            cursor: 'pointer',
                                                                                            '&:hover': {
                                                                                                color: (theme) =>
                                                                                                    theme.palette
                                                                                                        .secondary.main,
                                                                                            },
                                                                                        }}
                                                                                        onClick={() => {
                                                                                            setMode(Mode.edit);
                                                                                            setIdNote(item.Id ?? 0);
                                                                                            popupNoteHistory.setTrue();
                                                                                        }}
                                                                                    >
                                                                                        <IcEdit />
                                                                                    </Box>
                                                                                </Tooltip>
                                                                                <DangerTooltip
                                                                                    placement="top"
                                                                                    title="Delete"
                                                                                >
                                                                                    <Box
                                                                                        sx={{
                                                                                            cursor: 'pointer',
                                                                                        }}
                                                                                        onClick={() => {
                                                                                            setIdNote(item.Id ?? 0);
                                                                                            popUpConfirmDeleteNoteHistory.setTrue();
                                                                                        }}
                                                                                    >
                                                                                        <ICDelete fontSize={18} />
                                                                                    </Box>
                                                                                </DangerTooltip>
                                                                            </Stack>
                                                                        </>
                                                                    }
                                                                    align="left"
                                                                />
                                                            </>
                                                        );
                                                    }}
                                                />
                                            </Stack>
                                        }
                                    />
                                ),
                            },
                        ].map((item, index) => {
                            return (
                                <CustomTabPanel value={valueTabNotes} index={index}>
                                    {item.children}
                                </CustomTabPanel>
                            );
                        })}
                    </Box>

                    {/* Photo of camera as installed */}
                    <Grid
                        item
                        container
                        xs={12}
                        height={'100%'}
                        bgcolor={color.white}
                        padding={2}
                        sx={{ mt: 1 }}
                        borderRadius={'16px 16px 0px 0px'}
                    >
                        <Stack
                            borderBottom={'1px solid #E4E7EC'}
                            width={'100%'}
                            paddingRight={2}
                            direction={'row'}
                            justifyContent={'space-between'}
                            alignItems={'center'}
                        >
                            <Typography color={color.darkPrimary} variant="h5" width={'100%'} mb={1}>
                                Photo of camera as installed
                            </Typography>
                            {!isHideAction && (
                                <Tooltip title="Edit Photo of camera as installed" placement="top" arrow>
                                    <IconButton
                                        sx={{
                                            borderRadius: '3px',
                                            width: '24px',
                                            height: '24px',
                                            color: 'white',
                                            backgroundColor: '#2E7D32',
                                            ':hover': {
                                                backgroundColor: '#256428',
                                            },
                                            padding: '4px',
                                            '&.MuiIconButton-root.Mui-disabled': {
                                                backgroundColor: '#eee',
                                            },
                                        }}
                                        onClick={() => {
                                            popUpEditCameraPhotoInstalled.setTrue();
                                        }}
                                    >
                                        <IcEdit style={{ width: '14px', height: '14px' }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Stack>
                    </Grid>
                </Grid>
                <Box px={camera?.CameraInstalledPhotos?.length! > 0 ? 0 : 2} bgcolor={color.grey100}>
                    {camera?.CameraInstalledPhotos?.length! > 0 ? (
                        <ImageCarousel camera={camera} />
                    ) : (
                        <NoDataCameras
                            sx={{
                                borderRadius: '0px 0px 4px 4px',
                                height: '250px',
                                mb: 2,
                                bgcolor: color.white,
                                width: '100%',
                                border: 'none',
                            }}
                        />
                    )}
                </Box>
            </Stack>

            <PopUpCameraStatus
                open={popUpWarning.open}
                onClose={popUpWarning.onClose}
                isActivating={isActive !== Status.Active}
                onConfirm={handleOnConfirm}
            />

            <PopUpWarning
                {...popUpWarningVerification}
                title="Confirm"
                message="Are you sure you want to change the status of create verifications?"
                onConfirm={() => {
                    handleConfirmVerification(camera.Id!, isEnabled);
                    popUpWarningVerification.onClose();
                }}
            />
            <PopUpWarning
                {...popUpWarningEnableGroupMatching}
                title="Confirm"
                message="Are you sure you want to change the status of group matching?"
                onConfirm={() => {
                    handleConfirmEnableGroupMatching(camera.Id!, isEnabled);
                }}
            />

            <PopUpAddExemption
                {...popUpAddExemption}
                mode={mode}
                selectedExemption={selectedExemption}
                onClose={() => {
                    popUpAddExemption.onClose();
                }}
                onConfirm={(data) => {
                    const exemption: ExemptionPeriods = {
                        CameraId: camera.Id!,
                        Id: data.id,
                        EffectiveFrom: moment(data.from).toDate(),
                        EffectiveTo: moment(data.to).toDate(),
                        Notes: data.desc,
                    };

                    return props.onAddExemption?.(exemption);
                }}
            />

            <PopUpWarning
                {...popUpConfirmDelete}
                title="Confirm"
                message="Are you sure you want to delete this exemption?"
                isLoading={isDeletingExemption}
                onConfirm={async () => {
                    setIsDeletingExemption(true);
                    try {
                        if ((await props.onDeleteExemption?.(selectedExemption.Id!)) === false) return;
                        popUpConfirmDelete.onClose();
                    } finally {
                        setIsDeletingExemption(false);
                    }
                }}
            />

            <PopUpHistoryExemption
                {...popupHistoryExemption}
                onClose={() => {
                    popupHistoryExemption.onClose();
                }}
            />

            <PopUpNoteHistory
                {...popupNoteHistory}
                mode={mode}
                camera={camera}
                listNotes={notes ?? ({} as Paging<CameraNotes>)}
                idNote={idNote}
                handleAddOrUpdateNote={handleAddOrUpdateNote}
                valueTabNotes={valueTabNotes}
            />

            <PopUpWarning
                {...popUpConfirmDeleteNoteHistory}
                title="Confirm"
                message="Are you sure you want to delete this note?"
                onClose={() => {
                    setIdNote(0);
                    popUpConfirmDeleteNoteHistory.onClose();
                }}
                onConfirm={() => {
                    if (idNote) {
                        handleDeleteNote(idNote);
                        popUpConfirmDeleteNoteHistory.onClose();
                    } else {
                        pushError('Id note does not exist');
                    }
                }}
            />
            <PopUpEditCameraPhotoInstalled
                {...popUpEditCameraPhotoInstalled}
                camera={camera}
                onConfirm={async () => {
                    await cameraController.get(String(camera.Id!)).then((res) => {
                        setCamera(res);
                    });
                }}
            />

            <PopUpSwapMac {...popUpSwapMac} cameraId={camera.Id!} />

            <PopUpWarning
                {...popUpEditPatrolMode}
                title="Confirm"
                message="Are you sure you want to edit the patrol mode?"
                onClose={() => {
                    popUpEditPatrolMode.onClose();
                }}
                onConfirm={() => {
                    handleEditPatrolMode(patrolMode);
                }}
            />
        </>
    );
}

type BaseComponentDetailsProps = {
    title: string;
    content: ReactNode;
    style?: SxProps;
    styleTitle?: SxProps;
    isHideAction?: boolean;
    iconAdd?: ReactNode;
    iconHistory?: ReactNode;
    popupAdd?: UsePopUpReturnType;
    popupHistory?: UsePopUpReturnType;
    titleTooltipIconAdd?: string;
    titleTooltipIconHistory?: string;
    setMode?: Dispatch<SetStateAction<Mode | null>>;
};

export const BaseComponentDetails: React.FC<BaseComponentDetailsProps> = (props) => {
    const {
        title,
        content,
        style,
        styleTitle,
        isHideAction,
        iconAdd,
        iconHistory,
        setMode,
        popupAdd,
        popupHistory,
        titleTooltipIconAdd,
        titleTooltipIconHistory,
    } = props;
    return (
        <Grid
            item
            container
            xs={12}
            height={'100%'}
            bgcolor={color.white}
            padding={2}
            borderRadius={2}
            sx={{
                ...style,
            }}
        >
            <Stack
                borderBottom={'1px solid #E4E7EC'}
                width={'100%'}
                paddingRight={2}
                direction={'row'}
                justifyContent={'space-between'}
                alignItems={'center'}
                sx={{
                    ...styleTitle,
                }}
            >
                <Typography color={color.darkPrimary} variant="h5" width={'100%'} mb={1}>
                    {title}
                </Typography>
                {!isHideAction && (
                    <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1} mb={1}>
                        {iconAdd && (
                            <Tooltip title={titleTooltipIconAdd} placement="top" arrow>
                                <IconButton
                                    sx={{
                                        borderRadius: '3px',
                                        width: '24px',
                                        height: '24px',
                                        color: 'white',
                                        backgroundColor: '#2E7D32',
                                        ':hover': {
                                            backgroundColor: '#256428',
                                        },
                                        padding: '4px',
                                        '&.MuiIconButton-root.Mui-disabled': {
                                            backgroundColor: '#eee',
                                        },
                                    }}
                                    onClick={() => {
                                        setMode?.(Mode.create);
                                        popupAdd?.setTrue();
                                    }}
                                >
                                    {iconAdd}
                                </IconButton>
                            </Tooltip>
                        )}
                        {iconHistory && (
                            <Tooltip title={titleTooltipIconHistory} placement="top" arrow>
                                <IconButton
                                    sx={{
                                        borderRadius: '3px',
                                        width: '24px',
                                        height: '24px',
                                        color: 'white',
                                        backgroundColor: '#2E7D32',
                                        ':hover': {
                                            backgroundColor: '#256428',
                                        },
                                        padding: '4px',
                                        '&.MuiIconButton-root.Mui-disabled': {
                                            backgroundColor: '#eee',
                                        },
                                    }}
                                    onClick={() => {
                                        popupHistory?.setTrue();
                                    }}
                                >
                                    {iconHistory}
                                </IconButton>
                            </Tooltip>
                        )}
                    </Stack>
                )}
            </Stack>

            {content}
        </Grid>
    );
};
